import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import { Document as DocxDocument, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import { ChevronDown } from 'lucide-react';
import DocumentFileViewer from '../components/shared/DocumentFileViewer';

function contextField(context, ...keys) {
  for (const key of keys) {
    const value = context?.[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return 'Not specified';
}

const RevampResultPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [original, setOriginal] = useState('');
  const [revamped, setRevamped] = useState('');
  const [docType, setDocType] = useState('');
  const [revampData, setRevampData] = useState(null);
  const [enhancedPdfUrl, setEnhancedPdfUrl] = useState(null);
  const [enhancedDocxUrl, setEnhancedDocxUrl] = useState(null);
  const [originalFileUrl, setOriginalFileUrl] = useState(null);
  const [enhancedFormat, setEnhancedFormat] = useState('pdf');
  const navigate = useNavigate();
  const [actionsOpen, setActionsOpen] = useState(false);

  useEffect(() => {
    const fetchRevamp = async () => {
      setLoading(true);
      setError('');
      try {
        // First, get the document details
        const doc = await apiRequest(`documents/${id}`);
        if (!doc) {
          throw new Error('Document not found');
        }
        
        setDocType(doc.document_type);
        
        // Try to get revamp data from the new revamps endpoint
        try {
          const revampsResponse = await apiRequest(`documents/${id}/revamps`);
          if (revampsResponse && revampsResponse.revamps && revampsResponse.revamps.length > 0) {
            // Use the latest revamp
            const latestRevamp = revampsResponse.revamps[0];

            // Parse JSON fields if they are strings
            const originalObj = typeof latestRevamp.original_json === 'string'
              ? JSON.parse(latestRevamp.original_json)
              : latestRevamp.original_json;
            const improvedObj = typeof latestRevamp.improved_json === 'string'
              ? JSON.parse(latestRevamp.improved_json)
              : latestRevamp.improved_json;
            const contextObj = typeof latestRevamp.context_json === 'string'
              ? JSON.parse(latestRevamp.context_json)
              : latestRevamp.context_json;

            setRevampData({ ...latestRevamp, context_json: contextObj });
            setEnhancedPdfUrl(latestRevamp.pdf_url || null);
            setEnhancedDocxUrl(latestRevamp.docx_url || null);
            setOriginalFileUrl(
              revampsResponse.original_file_url
              || latestRevamp.original_file_url
              || doc.file_path
              || null,
            );

            // Extract original and revamped text from the parsed structure
            const originalText = originalObj?.text || '';
            
            // Handle both old format (text field) and new structured format
            let revampedData = null;
            if (improvedObj?.text) {
              // Old format: has text field (SOP/CV style)
              revampedData = improvedObj.text;
            } else if (improvedObj?.title && improvedObj?.sections && improvedObj?.parties) {
              // New structured format: has title, sections, parties (contracts)
              revampedData = improvedObj; // Pass the whole structured object
            } else if (improvedObj) {
              // Fallback: pass the whole object if it exists
              revampedData = improvedObj;
            }

            if (!revampedData) {
              setOriginal(originalText);
              setError('No revamped text found for this document.');
              return;
            }
            setOriginal(originalText);
            setRevamped(revampedData);
          } else {
            // Fallback to old structure if no revamps found
            if (doc.revamped_text) {
              // Check if revamped_text is a JSON object with a "revamped" property
              if (typeof doc.revamped_text === 'object' && doc.revamped_text.revamped) {
                setRevamped(doc.revamped_text.revamped);
              } else if (typeof doc.revamped_text === 'string') {
                // Fallback: if it's a string, use it directly
                setRevamped(doc.revamped_text);
              }
            } else if (doc.analysis_result && doc.analysis_result.revamped_text) {
              // Check if revamped text is stored in analysis_result
              if (typeof doc.analysis_result.revamped_text === 'object' && doc.analysis_result.revamped_text.revamped) {
                setRevamped(doc.analysis_result.revamped_text.revamped);
              } else if (typeof doc.analysis_result.revamped_text === 'string') {
                setRevamped(doc.analysis_result.revamped_text);
              }
            }
            
            // For original text, we need to extract it from the document
            // This might require a separate API call to get the original text
            if (doc.analysis_result && doc.analysis_result.original_text) {
              setOriginal(doc.analysis_result.original_text);
            }
          }
        } catch (revampError) {
          console.warn('Could not fetch revamp data:', revampError);
          // Fallback to old structure
          if (doc.revamped_text) {
            if (typeof doc.revamped_text === 'object' && doc.revamped_text.revamped) {
              setRevamped(doc.revamped_text.revamped);
            } else if (typeof doc.revamped_text === 'string') {
              setRevamped(doc.revamped_text);
            }
          }
        }
        
      } catch (err) {
        setError(err.message || 'Failed to load revamp result');
        toast.error(err.message || 'Failed to load revamp result');
      } finally {
        setLoading(false);
      }
    };

    fetchRevamp();
  }, [id]);

  // Helper function to extract course name from SOP content
  const extractCourseFromContent = (content) => {
    if (!content) return '';
    
    // Common patterns to look for course names in SOP content
    const patterns = [
      // "pursuing an MSc in Computer Science"
      /pursuing (?:an? )?(msc|mba|ma|phd|bachelor|master)[^.]*?in ([^.]+)/i,
      
      // "Master's in Computer Science"
      /(master'?s|phd|doctorate|bachelor'?s) (?:degree )?in ([^.]+)/i,
      
      // "applying for Computer Science program"
      /applying for (?:the )?([^.]+?) program/i,
      
      // "Computer Science with Artificial Intelligence"
      /(?:studying|pursuing|in) ([^.]*(?:computer science|engineering|business|law|medicine|data science|artificial intelligence)[^.]*)/i,
      
      // "MSc in Advanced Computer Science"
      /msc in ([^.]+)/i,
      
      // "Master of Science in [field]"
      /master of (?:science|arts|business|engineering) in ([^.]+)/i
    ];
    
    for (const pattern of patterns) {
      const match = content.match(pattern);
      if (match && match[2]) {
        let course = match[2].trim();
        // Clean up common suffixes
        course = course.replace(/\s+(at|from|in)\s+.*$/i, '');
        course = course.replace(/\s+(program|degree|course)$/i, '');
        return course;
      } else if (match && match[1]) {
        let course = match[1].trim();
        course = course.replace(/\s+(at|from|in)\s+.*$/i, '');
        course = course.replace(/\s+(program|degree|course)$/i, '');
        return course;
      }
    }
    
    return '';
  };

  // Helper function to extract SOP content from various JSON structures
  const extractSOPContent = (improvedObj, revamped) => {
    let bodyText = '';
    
    // Try different ways to extract the content
    if (typeof improvedObj === 'string') {
      bodyText = improvedObj;
    } else if (improvedObj.text) {
      bodyText = improvedObj.text;
    } else if (improvedObj.content) {
      bodyText = improvedObj.content;
    } else if (improvedObj.body) {
      bodyText = improvedObj.body;
    } else {
      // Combine all sections into one flowing text
      const sections = [
        improvedObj.introduction?.background,
        improvedObj.introduction?.current_role, 
        improvedObj.introduction?.motivation,
        improvedObj.academic_background,
        improvedObj.professional_experience,
        improvedObj.goals,
        improvedObj.program_interest,
        improvedObj.conclusion,
        improvedObj.summary
      ].filter(Boolean);
      
      if (sections.length > 0) {
        bodyText = sections.join('\n\n');
      } else {
        // Fallback: try to extract any text content from the object
        const textValues = Object.values(improvedObj)
          .filter(value => typeof value === 'string' && value.length > 50);
        bodyText = textValues.join('\n\n');
      }
    }
    
    // If still no content, use the raw revamped text
    if (!bodyText.trim()) {
      bodyText = typeof revamped === 'string' ? revamped : JSON.stringify(revamped, null, 2);
    }
    
    return bodyText;
  };

  const handleDownload = () => {
    if (!revamped) return;
    
    const blob = new Blob([revamped], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revamped-${docType}-${id}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Revamped document downloaded!');
  };

  // Helper function to replace placeholders with underlined spaces
  const replacePlaceholders = (text) => {
    if (!text) return text;
    
    // Clean and normalize text first
    let cleanedText = text
      // Normalize unicode characters
      .normalize('NFKD')
      // Replace smart quotes with regular quotes
      .replace(/[\u2018\u2019]/g, "'")  // Single quotes
      .replace(/[\u201C\u201D]/g, '"')  // Double quotes
      // Replace em/en dashes with regular hyphens
      .replace(/[\u2013\u2014]/g, '-')
      // Replace other problematic unicode characters
      .replace(/\u00A0/g, ' ')  // Non-breaking space
      .replace(/\u2026/g, '...')  // Ellipsis
      // Remove any remaining problematic characters that might cause issues in PDF
      .replace(/[^\x00-\x7F\u00C0-\u017F]/g, ''); // Allow basic Latin + Latin Extended-A characters
    
    // Replace various placeholder patterns with underlined spaces
    return cleanedText
      .replace(/\[Placeholder\]/gi, '________________________________________________________________________')
      .replace(/\[placeholder\]/gi, '________________________________________________________________________')
      .replace(/\[.*?\]/g, '________________________________________________________________________') // Replace any [text] with underlines
      .replace(/\{.*?\}/g, '________________________________________________________________________') // Replace any {text} with underlines
      .replace(/__+Date__+/gi, 'Date: ________________________') // Fix date placeholders
      .replace(/__+Name__+/gi, 'Name: ________________________________________________________________________') // Fix name placeholders
      .replace(/\[Date\]/gi, '________________________')
      .replace(/\[Name\]/gi, '________________________________________________________________________')
      .replace(/\[Address\]/gi, '________________________________________________________________________')
      .replace(/\[Insert.*?\]/gi, '________________________________________________________________________'); // Replace [Insert anything]
  };

  // Helper function to format numbered lists and bullet points
  const formatContentWithLists = (content) => {
    if (!content) return content;
    
    // Clean and normalize content first, then clean placeholders
    let formattedContent = replacePlaceholders(content);
    
    // Split into lines to process numbered lists
    const lines = formattedContent.split('\n');
    let result = '';
    let inList = false;
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Check if line starts with a number (e.g., "1. ", "2. ", etc.)
      const numberedListMatch = trimmedLine.match(/^(\d+)\.\s+(.+)/);
      // Check if line starts with letter list (e.g., "a. ", "b. ", etc.)
      const letterListMatch = trimmedLine.match(/^([a-z])\.\s+(.+)/i);
      // Check if line starts with bullet-like characters
      const bulletMatch = trimmedLine.match(/^[-•*]\s+(.+)/);
      
      if (numberedListMatch) {
        // Format as numbered list
        const [, number, text] = numberedListMatch;
        result += `\n${number}. ${text}\n`;
        inList = true;
      } else if (letterListMatch) {
        // Format as letter list  
        const [, letter, text] = letterListMatch;
        result += `\n    ${letter}. ${text}\n`;
        inList = true;
      } else if (bulletMatch) {
        // Format as bullet list
        const [, text] = bulletMatch;
        result += `\n• ${text}\n`;
        inList = true;
      } else if (trimmedLine === '' && inList) {
        // Empty line ends the list
        result += '\n';
        inList = false;
      } else if (trimmedLine !== '') {
        // Regular content
        if (inList && trimmedLine.length > 0) {
          // If we're in a list and this line doesn't start with a number/bullet,
          // it might be a continuation of the previous item
          result += `   ${trimmedLine}\n`;
        } else {
          result += `${trimmedLine}\n`;
          inList = false;
        }
      } else {
        result += '\n';
      }
    });
    
    return result.trim();
  };

  // Add a robust formatter for enhanced contract
  function formatEnhancedContract(json) {
    if (!json) return '';
    if (typeof json === 'string') return replacePlaceholders(json);
    
    // Handle old format with "text" field (SOP/CV style)
    if (json.text && typeof json.text === 'string') {
      // AI has provided the old standardized contract format with text field - return as is
      return replacePlaceholders(json.text);
    }
    
    // Handle NEW STRUCTURED FORMAT with title, parties, sections
    if (json.title && json.sections && json.parties) {
      let text = '';
      
      // HEADER - Contract Title
      text += `${json.title.toUpperCase()}\n`;
      text += '='.repeat(json.title.length) + '\n\n';
      
      // Note: No standalone PARTIES section. Parties are rendered inside INTRODUCTION.
      
      // SECTIONS
      if (json.sections && Array.isArray(json.sections)) {
        json.sections.forEach((section) => {
          // Main section header
          if (section.title) {
            text += `${section.number}. ${section.title.toUpperCase()}\n`;
            text += '-'.repeat(section.title.length + 4) + '\n';
          }
          
          // For INTRODUCTION: include parties inline and add a robust composed intro paragraph
          if (
            section.title &&
            section.title.toUpperCase() === 'INTRODUCTION' &&
            json.parties && Array.isArray(json.parties) && json.parties.length > 0
          ) {
            const agreementDate = json.agreementDate || new Date().toLocaleDateString();
            text += `This Agreement is entered into on ${agreementDate}, between:\n\n`;
            json.parties.forEach((party, index) => {
              text += `${index + 1}. ${replacePlaceholders(party.name)}`;
              if (party.type && party.type !== 'individual') {
                text += ` (${replacePlaceholders(party.type)})`;
              }
              if (party.address) {
                text += ` - ${replacePlaceholders(party.address)}`;
              }
              text += ` - referred to as the "${replacePlaceholders(party.role)}"\n`;
            });
            text += '\n';

            // Compose a robust INTRODUCTION sentence similar to legal style
            if (json.parties.length >= 2) {
              const p1 = json.parties[0];
              const p2 = json.parties[1];
              const titleCaps = (json.title || 'AGREEMENT').toUpperCase();
              const dateText = json.agreementDate || '____ day of __________ 20__';
              const composedIntro = `THIS ${titleCaps} is made this ${dateText}, BETWEEN ${replacePlaceholders(p1.name)} of ${replacePlaceholders(p1.address)} (hereinafter referred to as the "${replacePlaceholders(p1.role)}"), AND ${replacePlaceholders(p2.name)} of ${replacePlaceholders(p2.address)} (hereinafter referred to as the "${replacePlaceholders(p2.role)}").`;
              text += composedIntro + '\n\n';
            }
          }
          
                               // Section content
          if (section.content && section.content.trim()) {
            let cleanedContent = formatContentWithLists(section.content);
            if (section.title && section.title.toUpperCase() === 'INTRODUCTION') {
              // Remove duplicate basic intro lines (e.g., "THIS DEED OF ASSIGNMENT is made this ...")
              const lines = cleanedContent.split('\n');
              const titleCaps = (json.title || '').toUpperCase();
              const basicIntroRegex = new RegExp('^\\s*THIS\\s+' + (titleCaps ? titleCaps.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&').replace(/\s+/g, '\\s+') : '[A-Z\\s]+') + '\\s+is\\s+made\\s+this', 'i');
              const enteredIntoRegex = /^(\s*)This\s+Agreement\s+is\s+entered\s+into\s+on/i;
              const betweenLine = /^(\s*)between:$/i;
              const filtered = lines.filter(l => !basicIntroRegex.test(l) && !enteredIntoRegex.test(l) && !betweenLine.test(l.trim()));
              cleanedContent = filtered.join('\n');
            }
            if (cleanedContent.trim()) {
              text += `${cleanedContent}\n\n`;
            }
          }
          
                    // Subsections
          if (section.subsections && Array.isArray(section.subsections)) {
            section.subsections.forEach((subsection) => {
              text += `${subsection.number} ${replacePlaceholders(subsection.title)}\n`;
              
              // Handle numbered items if they exist
              if (subsection.numbered_items && Array.isArray(subsection.numbered_items)) {
                subsection.numbered_items.forEach((item) => {
                  text += `${item.number}. ${replacePlaceholders(item.content)}\n\n`;
                });
              } else if (subsection.content) {
                // Regular content
                text += `${formatContentWithLists(subsection.content)}\n\n`;
              }
              
              // Handle tables in subsections
              if (subsection.table) {
                const table = subsection.table;
                // Table headers
                text += table.headers.join(' | ') + '\n';
                text += '-'.repeat(table.headers.join(' | ').length) + '\n';
                
                // Table rows
                table.rows.forEach((row) => {
                  text += row.join(' | ') + '\n';
                });
                text += '\n';
              }
            });
          }
          
          // Special handling for EXECUTION section (works for either 5 or 6 depending on model)
          if (section.title && section.title.toUpperCase().includes("EXECUTION")) {
            text += '\nSIGNATURE SECTION\n';
            text += '='.repeat(17) + '\n\n';
            
            if (json.parties && Array.isArray(json.parties)) {
                             json.parties.forEach((party) => {
                 text += `${replacePlaceholders(party.role).toUpperCase()}:\n`;
                 text += '___________________________________    Date: _________________\n';
                 
                 let signerInfo = replacePlaceholders(party.name);
                 if (party.signerName && party.signerName !== party.name) {
                   signerInfo = replacePlaceholders(party.signerName);
                   if (party.signerTitle) {
                     signerInfo += `, ${replacePlaceholders(party.signerTitle)}`;
                   }
                 } else if (party.type && party.type !== 'individual') {
                   signerInfo += ` (${replacePlaceholders(party.type)})`;
                 }
                 
                 text += `${signerInfo}\n\n`;
               });
            }
          }
        });
      }
      
      return text.trim();
    }
    
    // Fallback: Handle old unstructured format or incomplete data
    let text = '';
    
    // HEADER SECTION
    const contractTitle = json.contract_title || 
                        json.title || 
                        json.agreement_title || 
                        'AGREEMENT';
    
    text += `${contractTitle.toUpperCase()}\n`;
    
    // Contract type subtitle
    const contractType = json.contract_type || json.type || json.agreement_type || '';
    if (contractType && contractType !== contractTitle) {
      text += `${contractType.charAt(0).toUpperCase() + contractType.slice(1)}\n`;
    }
    
    text += '\n';
    
    // No standalone PARTIES section in fallback; parties will be shown inside INTRODUCTION
    
    // MAIN SECTIONS
    const sections = json.sections || json.clauses || [];
    if (sections.length > 0) {
      sections.forEach((section, index) => {
        const sectionTitle = section.title || section.heading || `Section ${index + 1}`;
        text += `${index + 1}. ${sectionTitle.toUpperCase()}\n`;
        text += '-'.repeat(sectionTitle.length + 4) + '\n';

        // If this is INTRODUCTION and parties exist, inject parties here
        if (
          sectionTitle &&
          sectionTitle.toUpperCase() === 'INTRODUCTION' &&
          json.parties && Array.isArray(json.parties) && json.parties.length > 0
        ) {
          const agreementDate = json.agreementDate || json.agreement_date || json.date || json.effective_date || new Date().toLocaleDateString();
          text += `This Agreement is entered into on ${agreementDate}, between:\n\n`;
          json.parties.forEach((party, idx) => {
            text += `${idx + 1}. ${replacePlaceholders(party.name)}`;
            if (party.type && party.type !== 'individual') {
              text += ` (${replacePlaceholders(party.type)})`;
            }
            if (party.address) {
              text += ` - ${replacePlaceholders(party.address)}`;
            }
            text += ` - referred to as the "${replacePlaceholders(party.role)}"\n`;
          });
          text += '\n';
        }

        if (section.content || section.text) {
          text += `${section.content || section.text}\n\n`;
        }
      });
    }
    
    // TERMS AND CONDITIONS
    const terms = json.terms || json.conditions || [];
    if (terms.length > 0) {
      text += 'TERMS AND CONDITIONS\n';
      text += '='.repeat(20) + '\n';
      
      terms.forEach((term, index) => {
        const termText = typeof term === 'object' ? (term.text || term.content) : term;
        text += `${index + 1}. ${termText}\n`;
      });
      text += '\n';
    }
    
    // SIGNATURE BLOCK
    text += 'EXECUTION\n';
    text += '='.repeat(10) + '\n';
    
    const signatureBlock = json.signature_block || json.signatures || json.execution_block;
    
    if (signatureBlock) {
      text += `${signatureBlock}\n`;
    } else {
      text += 'IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.\n\n';
      
      // Standard signature lines
      if (json.parties) {
        const parties = json.parties;
        
        if (parties.company && parties.freelancer) {
          text += 'COMPANY:\n\n';
          text += '___________________________________    Date: _________________\n';
          text += `${parties.company.name || '[Company Name]'}\n\n`;
          
          text += 'CONTRACTOR:\n\n';
          text += '___________________________________    Date: _________________\n';
          text += `${parties.freelancer.name || '[Freelancer Name]'}\n`;
          
        } else if (parties.party1 && parties.party2) {
          text += 'PARTY 1:\n\n';
          text += '___________________________________    Date: _________________\n';
          text += `${parties.party1.name || '[Party 1 Name]'}\n\n`;
          
          text += 'PARTY 2:\n\n';
          text += '___________________________________    Date: _________________\n';
          text += `${parties.party2.name || '[Party 2 Name]'}\n`;
        } else {
          text += 'SIGNATURES:\n\n';
          Object.entries(parties).forEach(([key, party]) => {
            if (typeof party === 'object' && party.name) {
              text += `${key.toUpperCase()}:\n\n`;
              text += '___________________________________    Date: _________________\n';
              text += `${party.name}\n\n`;
            }
          });
        }
      } else {
        text += 'PARTY 1:\n\n';
        text += '___________________________________    Date: _________________\n';
        text += '[Party 1 Name]\n\n';
        
        text += 'PARTY 2:\n\n';
        text += '___________________________________    Date: _________________\n';
        text += '[Party 2 Name]\n';
      }
    }
    
    // Fallback: if nothing was formatted, show the raw JSON
    if (!text.trim()) {
      text = JSON.stringify(json, null, 2);
    }
    
    return text.trim();
  }

  // Add a robust formatter for enhanced SOP  
  function formatEnhancedSOP(json) {
    if (!json) return '';
    
    // If it's already a string, return it
    if (typeof json === 'string') return json;
    
    let text = '';
    
    // Try to build a coherent SOP from the JSON structure
    if (json.candidate_name && json.program && json.university) {
      text += `STATEMENT OF PURPOSE FOR ${json.candidate_name.toUpperCase()}: PURSUING ${json.program.toUpperCase()} AT ${json.university.toUpperCase()}\n\n`;
    }
    
    // Extract main content
    const content = extractSOPContent(json, '');
    if (content) {
      text += content;
    }
    
    // If no structured content was found, try to format the raw data
    if (!text.trim()) {
      if (json.text) text = json.text;
      else if (json.content) text = json.content;
      else if (json.body) text = json.body;
      else text = JSON.stringify(json, null, 2);
    }
    
    return text.trim();
  }

  function formatEnhancedCV(json) {
    if (!json) return '';
    
    // Handle new structured format (no text field)
    if (json.purpose && json.full_name && json.experience) {
      let text = '';
      
      // Header
      text += `${json.full_name.toUpperCase()}\n`;
      if (json.summary) {
        text += `${json.summary}\n`;
      }
      
      // Contact Information
      const contactInfo = [];
      if (json.phone) contactInfo.push(json.phone);
      if (json.email) contactInfo.push(json.email);
      if (json.linkedin) contactInfo.push(json.linkedin);
      if (json.address) contactInfo.push(json.address);
      
      if (contactInfo.length > 0) {
        text += `\n${contactInfo.join(' | ')}\n\n`;
      }
      
      // Career Objective/Summary
      if (json.summary) {
        text += `CAREER OBJECTIVE\n`;
        text += `${json.summary}\n\n`;
      }
      
      // Experience Section
      if (json.experience && json.experience.length > 0) {
        text += `EXPERIENCE\n\n`;
        
        json.experience.forEach((job, index) => {
          text += `${job.job_title || 'Job Title'}\n`;
          text += `${job.company || 'Company'}\n`;
          text += `${job.duration || 'Duration'}`;
          if (job.location) text += ` | ${job.location}`;
          text += '\n';
          
          if (job.achievements) {
            if (json.purpose === 'work' && Array.isArray(job.achievements)) {
              // Work format: bullet points
              job.achievements.forEach(achievement => {
                text += `• ${achievement}\n`;
              });
            } else if (json.purpose === 'study' && typeof job.achievements === 'string') {
              // Study format: paragraph
              text += `${job.achievements}\n`;
            } else {
              // Fallback: handle whatever format we got
              if (Array.isArray(job.achievements)) {
                job.achievements.forEach(achievement => {
                  text += `• ${achievement}\n`;
                });
              } else {
                text += `${job.achievements}\n`;
              }
            }
          }
          
          if (index < json.experience.length - 1) text += '\n';
        });
        text += '\n';
      }
      
      // Education Section
      if (json.education && json.education.length > 0) {
        text += `ACADEMIC QUALIFICATIONS\n\n`;
        
        json.education.forEach((edu, index) => {
          text += `${(edu.institution || 'Institution').toUpperCase()}\n`;
          text += `${edu.degree || 'Degree'} ${edu.duration || ''}\n`;
          if (edu.location) text += `${edu.location}\n`;
          if (edu.details) text += `${edu.details}\n`;
          
          if (index < json.education.length - 1) text += '\n';
        });
        text += '\n';
      }
      
      // Skills Section
      if (json.skills && json.skills.length > 0) {
        text += `SKILLS AND TECH STACK\n\n`;
        json.skills.forEach(skill => {
          text += `• ${skill}\n`;
        });
        text += '\n';
      }
      
      // Certifications Section
      if (json.certifications && json.certifications.length > 0) {
        text += `PROFESSIONAL CERTIFICATION/TRAINING\n\n`;
        json.certifications.forEach(cert => {
          text += `${cert.name}`;
          if (cert.issuer) text += ` (${cert.issuer})`;
          if (cert.date) text += ` ${cert.date}`;
          text += '\n';
        });
        text += '\n';
      }
      
      // Personal Information
      if (json.personal_info && Object.keys(json.personal_info).length > 0) {
        const personalInfo = json.personal_info;
        if (personalInfo.date_of_birth || personalInfo.gender || personalInfo.nationality) {
          text += `PERSONAL INFORMATION\n\n`;
          if (personalInfo.date_of_birth) text += `Date of Birth: ${personalInfo.date_of_birth}\n`;
          if (personalInfo.gender) text += `Gender: ${personalInfo.gender}\n`;
          if (personalInfo.nationality) text += `Nationality: ${personalInfo.nationality}\n`;
          if (personalInfo.interests) text += `Interests: ${personalInfo.interests}\n`;
          text += '\n';
        }
      }
      
      return text.trim();
    }
    
    // Handle old text format (should not happen with new system)
    if (json.text && typeof json.text === 'string') {
      console.warn('CV using old text format - this should not happen');
      return json.text;
    }
    
    // Fallback: convert unknown format to readable text
    console.warn('CV format not recognized, using fallback');
    return JSON.stringify(json, null, 2);
  }

  // Enhanced PDF download handler with standardized SOP titles
  const handleDownloadPDF = () => {
    console.log('PDF Generation Started for docType:', docType.toLowerCase());
    
    if (!revamped) {
      toast.error('No revamped content available for download.');
      return;
    }

    let improvedObj;
    let isPlainText = false;
    
    try {
      improvedObj = typeof revamped === 'string' ? JSON.parse(revamped) : revamped;
      console.log('Parsed JSON object:', improvedObj);
    } catch {
      isPlainText = true;
      improvedObj = revamped;
      console.log('Using plain text:', typeof improvedObj);
    }

    const doc = new jsPDF({ 
      unit: 'pt', 
      format: 'a4',
      compress: true,
      putOnlyUsedFonts: true 
    });
    let y = 50;
    const pageWidth = 595.28;
    const margin = 30; // Reduced from 50 to 30 for narrower margins
    const contentWidth = pageWidth - (2 * margin);

    // Helper functions for PDF generation
    const checkNewPage = () => {
      if (y > 750) {
        doc.addPage();
        y = 50;
      }
    };

    const addSectionHeader = (text) => {
      checkNewPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(text, margin, y);
      y += 25;
    };

    const addContent = (text) => {
      checkNewPage();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      
      const lines = doc.splitTextToSize(text, contentWidth);
      lines.forEach((line) => {
        if (y > 770) {
          doc.addPage();
          y = 50;
        }
        doc.text(line, margin, y);
        y += 16;
      });
      y += 5; // Extra space after content
    };

    console.log('Document type check:', docType.toLowerCase() === 'sop');

    // Document type specific formatting
    if (docType.toLowerCase() === 'sop') {
      console.log('SOP Processing Started - Detecting AI standardized format');
      
      // GET BODY CONTENT FROM IMPROVED_JSON
      let sopFullContent = '';
      
      if (isPlainText) {
        sopFullContent = improvedObj;
        console.log('Using plain text content, length:', sopFullContent.length);
      } else {
        console.log('Extracting content from improved_json');
        
        // Extract content from improved_json
        if (improvedObj.text) {
          sopFullContent = improvedObj.text;
          console.log('Found text field, length:', sopFullContent.length);
        } else if (improvedObj.content) {
          sopFullContent = improvedObj.content;
          console.log('Found content field, length:', sopFullContent.length);
        } else if (improvedObj.body) {
          sopFullContent = improvedObj.body;
          console.log('Found body field, length:', sopFullContent.length);
        } else {
          // Build from all text fields in improved_json
          const textSections = [];
          Object.entries(improvedObj).forEach(([key, value]) => {
            if (typeof value === 'string' && value.length > 30) {
              textSections.push(value);
            } else if (typeof value === 'object' && value !== null) {
              Object.values(value).forEach(subVal => {
                if (typeof subVal === 'string' && subVal.length > 30) {
                  textSections.push(subVal);
                }
              });
            }
          });
          sopFullContent = textSections.join('\n\n');
          console.log('Built from sections, total length:', sopFullContent.length);
        }
      }
      
      // Fallback if no content found
      if (!sopFullContent.trim()) {
        sopFullContent = typeof revamped === 'string' ? revamped : JSON.stringify(revamped, null, 2);
        console.log('Using fallback content, length:', sopFullContent.length);
      }
      
      console.log('Full SOP content length:', sopFullContent.length);
      
      // CHECK IF AI ALREADY PROVIDED STANDARDIZED FORMAT
      const hasStandardizedHeader = sopFullContent.includes('STATEMENT OF PURPOSE FOR A');
      console.log('Has standardized header:', hasStandardizedHeader);
      
      if (hasStandardizedHeader) {
        // AI has already provided the standardized format - use it directly
        console.log('Using AI standardized format directly');
        
        // Split content into lines to process header and body separately
        const lines = sopFullContent.split('\n').filter(line => line.trim());
        let headerProcessed = false;
        let nameProcessed = false;
        let bodyStartIndex = 0;
        
        // Find where the body content starts (after header and name)
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          
          // Skip the main header line
          if (line.includes('STATEMENT OF PURPOSE FOR A') && !headerProcessed) {
            headerProcessed = true;
            continue;
          }
          
          // Skip name line (usually comes after header and is short, all caps)
          if (headerProcessed && !nameProcessed && line.length < 50 && line === line.toUpperCase()) {
            nameProcessed = true;
            continue;
          }
          
          // Found start of body content
          if (headerProcessed && (nameProcessed || line.length > 50)) {
            bodyStartIndex = i;
            break;
          }
        }
        
        // Extract header line
        const headerLine = lines.find(line => line.includes('STATEMENT OF PURPOSE FOR A')) || '';
        
        // Extract name line
        const nameLine = lines.find((line, index) => {
          const trimmed = line.trim();
          return index > 0 && 
                index < bodyStartIndex && 
                trimmed.length < 50 && 
                trimmed === trimmed.toUpperCase() &&
                !trimmed.includes('STATEMENT OF PURPOSE');
        }) || '';
        
        // RENDER HEADER (Red, Bold, Centered)
        if (headerLine) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.setTextColor(220, 20, 60); // Crimson red
          
          const headerLines = doc.splitTextToSize(headerLine, contentWidth);
          headerLines.forEach((line, index) => {
            const lineWidth = doc.getTextWidth(line);
            const xPosition = (pageWidth - lineWidth) / 2;
            doc.text(line, xPosition, y + (index * 18));
          });
          
          y += (headerLines.length * 18) + 25;
        }
        
        // RENDER "BY" (Centered, Black, Normal)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0); // Black
        
        const byText = 'BY';
        const byWidth = doc.getTextWidth(byText);
        const byXPosition = (pageWidth - byWidth) / 2;
        doc.text(byText, byXPosition, y);
        y += 20;
        
        // RENDER NAME (Centered, Black, Bold)
        if (nameLine) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0); // Black
          
          const nameLines = doc.splitTextToSize(nameLine, contentWidth);
          nameLines.forEach((line, index) => {
            const lineWidth = doc.getTextWidth(line);
            const xPosition = (pageWidth - lineWidth) / 2;
            doc.text(line, xPosition, y + (index * 16));
          });
          
          y += (nameLines.length * 16) + 30;
        }
        
        // RENDER BODY (Black, Normal, Justified)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        
        // Get body content (everything after header and name)
        const bodyLines = lines.slice(bodyStartIndex);
        const bodyContent = bodyLines.join('\n');
        
        console.log('Body content length:', bodyContent.length);
        console.log('Body starts with:', bodyContent.substring(0, 100));
        
        // Process body paragraphs
        const paragraphs = bodyContent.split(/\n\s*\n/).filter(p => p.trim().length > 0);
        console.log('Processing body paragraphs:', paragraphs.length);
        
        paragraphs.forEach((paragraph, pIndex) => {
          const cleanParagraph = paragraph.trim();
          if (!cleanParagraph) return;
          
          const lines = doc.splitTextToSize(cleanParagraph, contentWidth);
          
          lines.forEach((line, lIndex) => {
            // Check for page break
            if (y > 780) {
              doc.addPage();
              y = 50;
            }
            
            const cleanLine = line.trim();
            if (!cleanLine) return;
            
            const isLastLine = lIndex === lines.length - 1;
            
            if (!isLastLine && lines.length > 1) {
              // Smart justification - only justify if spacing won't be too wide
              const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
              
              if (words.length > 1) {
                const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                const spaceToDistribute = contentWidth - totalWordWidth;
                const spacePerGap = spaceToDistribute / (words.length - 1);
                const normalSpaceWidth = doc.getTextWidth(' ');
                
                // Only justify if the space per gap is not more than 3x normal space width
                // This prevents ugly wide gaps between words
                if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                  let currentX = margin;
                  words.forEach((word, wIndex) => {
                    doc.text(word, currentX, y);
                    if (wIndex < words.length - 1) {
                      currentX += doc.getTextWidth(word) + spacePerGap;
                    }
                  });
                } else {
                  // Left align if justification would create ugly spacing
                  doc.text(cleanLine, margin, y);
                }
              } else {
                doc.text(cleanLine, margin, y);
              }
            } else {
              // Last line of paragraph - left align
              doc.text(cleanLine, margin, y);
            }
            
            y += 20; // IMPROVED: Increased from 18 to 20 for better line spacing
          });
          
          // Space between paragraphs - IMPROVED: Much better paragraph separation
          if (pIndex < paragraphs.length - 1) {
            y += 45; // IMPROVED: Increased from 30 to 45 for much better paragraph separation
          }
        });
        
      } else {
        // Fallback: AI didn't provide standardized format, build it manually
        console.log('Building manual header from context data');
        
        // GET HEADER INFO FROM CONTEXT_JSON
        let courseName = '';
        let institutionName = '';
        let programType = 'MASTER\'S PROGRAM';
        let candidateName = '';
        
        if (revampData && revampData.context_json) {
          const context = revampData.context_json;
          courseName = context.course_of_study || context.program || context.field || '';
          institutionName = context.institution || context.university || '';
          
          const contextProgramType = context.program_type || '';
          if (contextProgramType) {
            const lowerType = contextProgramType.toLowerCase();
            if (lowerType.includes('phd') || lowerType.includes('doctorate') || lowerType.includes('doctoral')) {
              programType = 'PHD PROGRAM';
            } else if (lowerType.includes('bachelor') || lowerType.includes('undergraduate')) {
              programType = 'BACHELOR\'S PROGRAM';
            } else if (lowerType.includes('mba')) {
              programType = 'MBA PROGRAM';
            } else if (lowerType.includes('master')) {
              programType = 'MASTER\'S PROGRAM';
            }
          }
        }
        
        // Try to extract candidate name from content
        if (!isPlainText) {
          candidateName = improvedObj.candidate_name || 
                        improvedObj.name || 
                        improvedObj.applicant_name || 
                        improvedObj.student_name || '';
        }
        
        // RENDER MANUAL HEADER
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(220, 20, 60); // Crimson red
        
        let mainTitle = '';
        if (courseName && institutionName) {
          mainTitle = `STATEMENT OF PURPOSE FOR A ${programType} IN ${courseName.toUpperCase()} AT ${institutionName.toUpperCase()}`;
        } else if (courseName) {
          mainTitle = `STATEMENT OF PURPOSE FOR A ${programType} IN ${courseName.toUpperCase()}`;
        } else {
          mainTitle = `STATEMENT OF PURPOSE FOR A ${programType}`;
        }
        
        const mainTitleLines = doc.splitTextToSize(mainTitle, contentWidth);
        mainTitleLines.forEach((line, index) => {
          const lineWidth = doc.getTextWidth(line);
          const xPosition = (pageWidth - lineWidth) / 2;
          doc.text(line, xPosition, y + (index * 18));
        });
        
        y += (mainTitleLines.length * 18) + 25;
        
        // BY
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        
        const byText = 'BY';
        const byWidth = doc.getTextWidth(byText);
        const byXPosition = (pageWidth - byWidth) / 2;
        doc.text(byText, byXPosition, y);
        y += 20;
        
        // NAME
        if (candidateName) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          
          const nameTitle = candidateName.toUpperCase();
          const nameTitleLines = doc.splitTextToSize(nameTitle, contentWidth);
          
          nameTitleLines.forEach((line, index) => {
            const lineWidth = doc.getTextWidth(line);
            const xPosition = (pageWidth - lineWidth) / 2;
            doc.text(line, xPosition, y + (index * 16));
          });
          
          y += (nameTitleLines.length * 16) + 30;
        }
        
        // BODY
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        
        const paragraphs = sopFullContent.split(/\n\s*\n/).filter(p => p.trim().length > 0);
        
        paragraphs.forEach((paragraph, pIndex) => {
          const cleanParagraph = paragraph.trim();
          if (!cleanParagraph) return;
          
          const lines = doc.splitTextToSize(cleanParagraph, contentWidth);
          
          lines.forEach((line, lIndex) => {
            if (y > 780) {
              doc.addPage();
              y = 50;
            }
            
            const cleanLine = line.trim();
            if (!cleanLine) return;
            
            const isLastLine = lIndex === lines.length - 1;
            
            if (!isLastLine && lines.length > 1) {
              // Smart justification - only justify if spacing won't be too wide
              const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
              
              if (words.length > 1) {
                const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                const spaceToDistribute = contentWidth - totalWordWidth;
                const spacePerGap = spaceToDistribute / (words.length - 1);
                const normalSpaceWidth = doc.getTextWidth(' ');
                
                // Only justify if the space per gap is not more than 3x normal space width
                // This prevents ugly wide gaps between words
                if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                  let currentX = margin;
                  words.forEach((word, wIndex) => {
                    doc.text(word, currentX, y);
                    if (wIndex < words.length - 1) {
                      currentX += doc.getTextWidth(word) + spacePerGap;
                    }
                  });
                } else {
                  // Left align if justification would create ugly spacing
                  doc.text(cleanLine, margin, y);
                }
              } else {
                doc.text(cleanLine, margin, y);
              }
            } else {
              doc.text(cleanLine, margin, y);
            }
            
            y += 20; // IMPROVED: Increased from 18 to 20 for better line spacing
          });
          
          // Space between paragraphs - IMPROVED: Much better paragraph separation
          if (pIndex < paragraphs.length - 1) {
            y += 45; // IMPROVED: Increased from 30 to 45 for much better paragraph separation
          }
        });
      }
      
      console.log('SOP Processing Complete - Used AI standardized format');
    } 
    else if (docType.toLowerCase() === 'contract') {
      console.log('Contract Processing Started - Structured JSON Format');
      
      // Check if we have structured JSON format
      const isStructured = improvedObj.title && improvedObj.sections && improvedObj.parties;
      
      if (isStructured) {
        console.log('Using structured contract JSON format');
        
        // ====== FILTER AND RENUMBER SECTIONS ======
        // Helper function to check if content is meaningful
        const hasContent = (content) => {
          return content && content.trim() && content.trim() !== '{}' && content.trim() !== '';
        };
        
        // Helper function to check if subsection has meaningful content
        const subsectionHasContent = (subsection) => {
          return (hasContent(subsection.content)) || 
                 (subsection.numbered_items && Array.isArray(subsection.numbered_items) && subsection.numbered_items.length > 0) ||
                 (subsection.table && subsection.table.headers && subsection.table.rows);
        };
        
        // Helper function to check if section has meaningful content
        const sectionHasContent = (section) => {
          const hasDirectContent = hasContent(section.content);
          const hasValidSubsections = section.subsections && 
                                      Array.isArray(section.subsections) && 
                                      section.subsections.some(subsectionHasContent);
          return hasDirectContent || hasValidSubsections;
        };
        
        // Filter out empty sections and subsections
        const filteredSections = improvedObj.sections
          .filter(sectionHasContent)
          .map((section, sectionIndex) => {
            // Filter subsections for this section
            const filteredSubsections = section.subsections ? 
              section.subsections.filter(subsectionHasContent) : [];
            
            // Renumber subsections
            const renumberedSubsections = filteredSubsections.map((subsection, subIndex) => ({
              ...subsection,
              number: `${sectionIndex + 1}.${subIndex + 1}`,
              originalNumber: subsection.number
            }));
            
            return {
              ...section,
              number: (sectionIndex + 1).toString(),
              originalNumber: section.number,
              subsections: renumberedSubsections
            };
          });
        
        console.log(`Filtered sections: ${filteredSections.length} out of ${improvedObj.sections.length} original sections`);
        
        // ====== CONTRACT HEADER ======
        // Title - BLACK and CENTERED
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0); // BLACK
        
        const title = replacePlaceholders(improvedObj.title) || 'CONTRACT AGREEMENT';
        const titleLines = doc.splitTextToSize(title, contentWidth);
        titleLines.forEach((line, index) => {
          const lineWidth = doc.getTextWidth(line);
          const xPosition = (pageWidth - lineWidth) / 2;
          doc.text(line, xPosition, y + (index * 20));
        });
        
        y += (titleLines.length * 20) + 30;
        
        // ====== PROCESS FILTERED SECTIONS ======
        console.log('Total filtered sections to process:', filteredSections.length);
        filteredSections.forEach((section, sIndex) => {
          console.log(`Processing filtered section ${sIndex + 1}:`, section.number, section.title, `(originally ${section.originalNumber})`);
          
          // Check for page break
          if (y > 700) {
            console.log('Adding new page at section:', section.number);
            doc.addPage();
            y = 50;
          }
          
          // Main section header (using new numbering)
          if (section.title) {
            y += 20; // Extra space before main sections
            
            console.log(`Rendering section header at y=${y}:`, section.number, section.title);
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 100, 200); // BLUE for section headers
            
            const headerText = `${section.number}. ${section.title}`;
            doc.text(headerText, margin, y);
            y += 20;
          }
          
          // Section content - JUSTIFIED (only if not empty)
          if (section.content && section.content.trim()) {
            console.log(`Rendering section content for ${section.number}:`, section.content.substring(0, 100) + '...');
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            
            // If INTRODUCTION, prepend robust intro before the provided content
            if (section.title && section.title.toUpperCase() === 'INTRODUCTION' && improvedObj.parties && improvedObj.parties.length >= 2) {
              const p1 = improvedObj.parties[0];
              const p2 = improvedObj.parties[1];
              const titleCaps = (improvedObj.title || 'AGREEMENT').toUpperCase();
              const dateText = improvedObj.agreementDate || '____ day of ____________ 20__';
              const successorsText = (p) => (p.type && ['company','corporate','organisation','organization','limited','ltd','llc'].includes(p.type.toLowerCase())) ? 'its successors and assigns' : 'her/heirs, personal representatives and successors-in-title';
              const robustIntro = `THIS ${titleCaps} is made this ${dateText} BETWEEN ${replacePlaceholders(p1.name)} of ${replacePlaceholders(p1.address)} (hereinafter referred to as the "${replacePlaceholders(p1.role)}" whose expression shall where the context so admit include ${successorsText(p1)}) of the ONE PART; AND ${replacePlaceholders(p2.name)} of ${replacePlaceholders(p2.address)} (hereinafter referred to as the "${replacePlaceholders(p2.role)}" whose expression shall where the context so admit include ${successorsText(p2)}) of the OTHER PART.`;
              const robustLines = doc.splitTextToSize(robustIntro, contentWidth);
              robustLines.forEach((line) => {
                if (y > 770) { doc.addPage(); y = 50; }
                doc.text(line, margin, y);
                y += 14;
              });
              y += 8;
            }

            // Skip printing raw INTRODUCTION content (to avoid duplicate basic intro)
            if (!(section.title && section.title.toUpperCase() === 'INTRODUCTION')) {
              const cleanedContent = formatContentWithLists(section.content);
              const contentLines = doc.splitTextToSize(cleanedContent, contentWidth);
              contentLines.forEach((line, lineIndex) => {
                if (y > 770) {
                  doc.addPage();
                  y = 50;
                }
                
                const cleanLine = line.trim();
                if (!cleanLine) return;
                
                const isLastLine = lineIndex === contentLines.length - 1;
                
                // Apply justification for non-last lines
                if (!isLastLine && contentLines.length > 1) {
                  const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                  
                  if (words.length > 1) {
                    const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                    const spaceToDistribute = contentWidth - totalWordWidth;
                    const spacePerGap = spaceToDistribute / (words.length - 1);
                    const normalSpaceWidth = doc.getTextWidth(' ');
                    
                    // Only justify if the space per gap is reasonable
                    if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                      let currentX = margin;
                      words.forEach((word, wIndex) => {
                        doc.text(word, currentX, y);
                        if (wIndex < words.length - 1) {
                          currentX += doc.getTextWidth(word) + spacePerGap;
                        }
                      });
                    } else {
                      // Left align if justification would create ugly spacing
                      doc.text(cleanLine, margin, y);
                    }
                  } else {
                    doc.text(cleanLine, margin, y);
                  }
                } else {
                  // Last line - left align
                  doc.text(cleanLine, margin, y);
                }
                
                y += 14;
              });
              y += 8;
            }
          }
          
          // Process filtered subsections - ONLY PROCESS if they exist and have content
          if (section.subsections && section.subsections.length > 0) {
            console.log(`Processing ${section.subsections.length} filtered subsections for section ${section.number}`);
            section.subsections.forEach((subsection) => {
              console.log(`Processing subsection:`, subsection.number, subsection.title, `(originally ${subsection.originalNumber})`);
              if (y > 720) {
                console.log('Adding new page for subsection:', subsection.number);
                doc.addPage();
                y = 50;
              }
              
              // Subsection header - ALWAYS RENDER (since we filtered, all remaining have content)
              if (subsection.title && subsection.title.trim()) {
                y += 12;
                
                console.log(`Rendering subsection at y=${y}:`, subsection.number, subsection.title);
                
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(0, 100, 200); // BLUE for subsection numbers
                
                const subHeaderText = `${subsection.number} ${replacePlaceholders(subsection.title)}`;
                doc.text(subHeaderText, margin, y);
                y += 16;
              }
              
              // Reset font for content
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(11);
              doc.setTextColor(0, 0, 0);
              
              // Handle numbered items if they exist - PRIORITY OVER CONTENT
              if (subsection.numbered_items && Array.isArray(subsection.numbered_items) && subsection.numbered_items.length > 0) {
                console.log(`Rendering ${subsection.numbered_items.length} numbered items for ${subsection.number}`);
                
                subsection.numbered_items.forEach((item, itemIndex) => {
                  console.log(`Processing item ${itemIndex + 1}:`, item);
                  
                  // Validate item content
                  if (!item || !item.content || !item.number) {
                    console.warn('Skipping invalid numbered item:', item);
                    return;
                  }
                  
                  if (y > 750) {
                    doc.addPage();
                    y = 50;
                  }
                  
                  // Add space before each numbered item (except first)
                  if (itemIndex > 0) {
                    y += 8;
                  }
                  
                  const itemText = `${item.number}. ${replacePlaceholders(item.content)}`;
                  console.log(`Item text length: ${itemText.length}, first 100 chars:`, itemText.substring(0, 100));
                  const itemLines = doc.splitTextToSize(itemText, contentWidth);
                  console.log(`Split into ${itemLines.length} lines`);
                  
                  itemLines.forEach((line, lineIndex) => {
                    if (y > 770) {
                      doc.addPage();
                      y = 50;
                    }
                    
                    const cleanLine = line.trim();
                    if (!cleanLine) {
                      console.log(`Skipping empty line ${lineIndex} in item ${itemIndex + 1}`);
                      return;
                    }
                    
                    console.log(`Rendering line ${lineIndex + 1}/${itemLines.length}: "${cleanLine.substring(0, 50)}..."`);
                  
                    const isLastLine = lineIndex === itemLines.length - 1;
                    
                    // Apply justification for non-last lines
                    if (!isLastLine && itemLines.length > 1) {
                      const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                      
                      if (words.length > 1) {
                        const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                        const spaceToDistribute = contentWidth - totalWordWidth;
                        const spacePerGap = spaceToDistribute / (words.length - 1);
                        const normalSpaceWidth = doc.getTextWidth(' ');
                        
                        if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                          let currentX = margin;
                          words.forEach((word, wIndex) => {
                            doc.text(word, currentX, y);
                            if (wIndex < words.length - 1) {
                              currentX += doc.getTextWidth(word) + spacePerGap;
                            }
                          });
                        } else {
                          doc.text(cleanLine, margin, y);
                        }
                      } else {
                        doc.text(cleanLine, margin, y);
                      }
                    } else {
                      doc.text(cleanLine, margin, y);
                    }
                    
                    y += 14;
                  });
                  
                  y += 8; // Extra space after each numbered item
                });
              } 
              // Handle regular subsection content ONLY if no numbered_items exist
              else if (subsection.content && subsection.content.trim()) {
                // Special handling for signature content - don't render as regular text
                if (subsection.title && subsection.title.toLowerCase().includes('signature')) {
                  console.log(`Skipping signature content rendering - will be handled by special signature section`);
                  // Skip rendering signature content as regular text since it will be handled by the special section
                } else {
                  console.log(`Rendering subsection content for ${subsection.number}:`, subsection.content.substring(0, 100) + '...');
                  
                  const cleanedSubContent = formatContentWithLists(subsection.content);
                  const subContentLines = doc.splitTextToSize(cleanedSubContent, contentWidth);
                subContentLines.forEach((line, lineIndex) => {
                  if (y > 770) {
                    doc.addPage();
                    y = 50;
                  }
                  
                  const cleanLine = line.trim();
                  if (!cleanLine) return;
                  
                  const isLastLine = lineIndex === subContentLines.length - 1;
                  
                  // Apply justification for non-last lines
                  if (!isLastLine && subContentLines.length > 1) {
                    const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                    
                    if (words.length > 1) {
                      const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                      const spaceToDistribute = contentWidth - totalWordWidth;
                      const spacePerGap = spaceToDistribute / (words.length - 1);
                      const normalSpaceWidth = doc.getTextWidth(' ');
                      
                      // Only justify if the space per gap is reasonable
                      if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                        let currentX = margin;
                        words.forEach((word, wIndex) => {
                          doc.text(word, currentX, y);
                          if (wIndex < words.length - 1) {
                            currentX += doc.getTextWidth(word) + spacePerGap;
                          }
                        });
                      } else {
                        // Left align if justification would create ugly spacing
                        doc.text(cleanLine, margin, y);
                      }
                    } else {
                      doc.text(cleanLine, margin, y);
                    }
                  } else {
                    // Last line - left align
                    doc.text(cleanLine, margin, y);
                  }
                  
                  y += 14;
                });
                }
              }
              
              // Handle tables in subsections (e.g., compensation table)
              if (subsection.table) {
                y += 10;
                
                // Table headers
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                
                const table = subsection.table;
                const colWidth = contentWidth / table.headers.length;
                
                // Draw header row
                table.headers.forEach((header, index) => {
                  doc.text(header, margin + (index * colWidth), y);
                });
                
                y += 15;
                
                // Draw line under headers
                doc.setLineWidth(0.5);
                doc.line(margin, y - 5, margin + contentWidth, y - 5);
                
                // Draw data rows
                doc.setFont('helvetica', 'normal');
                table.rows.forEach((row) => {
                  row.forEach((cell, index) => {
                    doc.text(cell, margin + (index * colWidth), y);
                  });
                  y += 14;
                });
                
                // Draw bottom line
                doc.line(margin, y, margin + contentWidth, y);
                y += 10;
              }
              
              y += 8;
            });
          }
          
          // Special handling for EXECUTION section - ADD signatures AFTER normal processing
          if (section.title && section.title.toUpperCase() === "EXECUTION") {
            console.log('Adding professional signature section for EXECUTION');
            
            // Check if we have special signature content in subsections
            const signatureSubsection = section.subsections?.find(sub => 
              sub.title && sub.title.toLowerCase().includes('signature')
            );
            
            if (signatureSubsection && signatureSubsection.content && signatureSubsection.content.trim()) {
              // Use the detailed signature content from the JSON but format it professionally
              console.log('Using detailed signature content from JSON with professional formatting');
              
              // Add extra space before signatures
              y += 30;
              
              // Parse the signature content to extract structured information
              const sigContent = replacePlaceholders(signatureSubsection.content);
              
              // Check if we need a new page for signatures
              if (y > 600) {
                doc.addPage();
                y = 50;
              }
              
              // Add "IN WITNESS WHEREOF" header if it exists in content
              if (sigContent.includes('IN WITNESS WHEREOF') || sigContent.includes('IN WITNESS OF WHICH')) {
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(11);
                doc.setTextColor(0, 0, 0);
                
                const witnessText = 'IN WITNESS WHEREOF the Parties have hereunto set their respective hands and seals the day and year first mentioned.';
                const witnessLines = doc.splitTextToSize(witnessText, contentWidth);
                witnessLines.forEach((line, lineIndex) => {
                  const cleanLine = line.trim();
                  if (!cleanLine) return;
                  
                  const isLastLine = lineIndex === witnessLines.length - 1;
                  
                  // Apply justification for non-last lines
                  if (!isLastLine && witnessLines.length > 1) {
                    const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                    
                    if (words.length > 1) {
                      const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                      const spaceToDistribute = contentWidth - totalWordWidth;
                      const spacePerGap = spaceToDistribute / (words.length - 1);
                      const normalSpaceWidth = doc.getTextWidth(' ');
                      
                      if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                        let currentX = margin;
                        words.forEach((word, wIndex) => {
                          doc.text(word, currentX, y);
                          if (wIndex < words.length - 1) {
                            currentX += doc.getTextWidth(word) + spacePerGap;
                          }
                        });
                      } else {
                        doc.text(cleanLine, margin, y);
                      }
                    } else {
                      doc.text(cleanLine, margin, y);
                    }
                  } else {
                    doc.text(cleanLine, margin, y);
                  }
                  y += 14;
                });
                y += 20;
              }
              
              // Professional signature blocks for each party
              if (improvedObj.parties && improvedObj.parties.length > 0) {
                improvedObj.parties.forEach((party, partyIndex) => {
                  // Ensure each signature block starts on the same page if possible
                  if (y > 650) {
                    doc.addPage();
                    y = 50;
                  }
                  
                  // Party signature header
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(11);
                  doc.setTextColor(0, 0, 0);
                  
                  const partyHeader = `SIGNED, SEALED AND DELIVERED by the within named ${party.role.toUpperCase()}:`;
                  doc.text(partyHeader, margin, y);
                  y += 20;
                  
                  // Name field
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(10);
                  
                  let partyName = replacePlaceholders(party.name);
                  if (party.signerName && party.signerName !== party.name) {
                    partyName = replacePlaceholders(party.signerName);
                  }
                  
                  doc.text('Name: ' + partyName, margin, y);
                  y += 16;
                  
                  // Address field (if available)
                  if (party.address) {
                    const addressLines = doc.splitTextToSize('Address: ' + replacePlaceholders(party.address), contentWidth - 100);
                    addressLines.forEach((line) => {
                      doc.text(line, margin, y);
                      y += 12;
                    });
                    y += 4;
                  } else {
                    doc.text('Address: ________________________________________________________________________', margin, y);
                    y += 16;
                  }
                  
                  // Occupation field
                  let occupation = '';
                  if (party.signerTitle) {
                    occupation = replacePlaceholders(party.signerTitle);
                  } else if (party.type && party.type !== 'individual') {
                    occupation = replacePlaceholders(party.type);
                  }
                  
                  if (occupation) {
                    doc.text('Occupation: ' + occupation, margin, y);
                  } else {
                    doc.text('Occupation: ________________________________________________________________________', margin, y);
                  }
                  y += 16;
                  
                  // Signature line with proper formatting
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(10);
                  
                  // Draw actual signature line
                  doc.setLineWidth(0.5);
                  doc.setDrawColor(0, 0, 0);
                  doc.line(margin + 60, y + 5, margin + 300, y + 5);
                  
                  // Date line
                  doc.line(margin + 350, y + 5, margin + 480, y + 5);
                  
                  doc.text('Signature:', margin, y);
                  doc.text('Date:', margin + 320, y);
                  y += 20;
                  
                  // Add witness section for each party
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(9);
                  doc.text('IN THE PRESENCE OF:', margin + 20, y);
                  y += 15;
                  
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(9);
                  
                  // Witness name line
                  doc.text('Name: __________________________________________________________________________', margin + 20, y);
                  y += 12;
                  
                  // Witness address line
                  doc.text('Address: __________________________________________________________________________', margin + 20, y);
                  y += 12;
                  
                  // Witness occupation line
                  doc.text('Occupation: __________________________________________________________________________', margin + 20, y);
                  y += 12;
                  
                  // Witness signature and date lines
                  doc.setLineWidth(0.3);
                  doc.line(margin + 80, y + 5, margin + 240, y + 5); // Signature line
                  doc.line(margin + 270, y + 5, margin + 400, y + 5); // Date line
                  
                  doc.text('Signature:', margin + 20, y);
                  doc.text('Date:', margin + 250, y);
                  y += 20;
                  
                  // Witness tel line
                  doc.text('Tel: __________________________________________________________________________', margin + 20, y);
                  y += 25;
                  
                  // Add extra space between parties
                  if (partyIndex < improvedObj.parties.length - 1) {
                    y += 20;
                  }
                });
              }
              
            } else {
              // Fallback to professional signature layout even without detailed content
              console.log('Using professional signature layout with basic party information');
              
              // Add extra space before signatures
              y += 30;
              
              // Check if we need a new page for signatures
              if (y > 600) {
                doc.addPage();
                y = 50;
              }
              
              // Add "IN WITNESS WHEREOF" header
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(11);
              doc.setTextColor(0, 0, 0);
              
              const witnessText = 'IN WITNESS WHEREOF the Parties have hereunto set their respective hands and seals the day and year first mentioned.';
              const witnessLines = doc.splitTextToSize(witnessText, contentWidth);
              witnessLines.forEach((line, lineIndex) => {
                const cleanLine = line.trim();
                if (!cleanLine) return;
                
                const isLastLine = lineIndex === witnessLines.length - 1;
                
                // Apply justification for non-last lines
                if (!isLastLine && witnessLines.length > 1) {
                  const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                  
                  if (words.length > 1) {
                    const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                    const spaceToDistribute = contentWidth - totalWordWidth;
                    const spacePerGap = spaceToDistribute / (words.length - 1);
                    const normalSpaceWidth = doc.getTextWidth(' ');
                    
                    if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                      let currentX = margin;
                      words.forEach((word, wIndex) => {
                        doc.text(word, currentX, y);
                        if (wIndex < words.length - 1) {
                          currentX += doc.getTextWidth(word) + spacePerGap;
                        }
                      });
                    } else {
                      doc.text(cleanLine, margin, y);
                    }
                  } else {
                    doc.text(cleanLine, margin, y);
                  }
                } else {
                  doc.text(cleanLine, margin, y);
                }
                y += 14;
              });
              y += 25;
              
              // Professional signature blocks for each party
              if (improvedObj.parties && improvedObj.parties.length > 0) {
                improvedObj.parties.forEach((party, partyIndex) => {
                  // Ensure each signature block starts on the same page if possible
                  if (y > 650) {
                    doc.addPage();
                    y = 50;
                  }
                  
                  // Party signature header
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(11);
                  doc.setTextColor(0, 0, 0);
                  
                  const partyHeader = `SIGNED, SEALED AND DELIVERED by the within named ${party.role.toUpperCase()}:`;
                  doc.text(partyHeader, margin, y);
                  y += 20;
                  
                  // Name field
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(10);
                  
                  let partyName = replacePlaceholders(party.name);
                  if (party.signerName && party.signerName !== party.name) {
                    partyName = replacePlaceholders(party.signerName);
                  }
                  
                  doc.text('Name: ' + partyName, margin, y);
                  y += 16;
                  
                  // Address field
                  if (party.address) {
                    const addressLines = doc.splitTextToSize('Address: ' + replacePlaceholders(party.address), contentWidth - 100);
                    addressLines.forEach((line) => {
                      doc.text(line, margin, y);
                      y += 12;
                    });
                    y += 4;
                  } else {
                    doc.text('Address: ________________________________', margin, y);
                    y += 16;
                  }
                  
                  // Occupation field
                  let occupation = '';
                  if (party.signerTitle) {
                    occupation = replacePlaceholders(party.signerTitle);
                  } else if (party.type && party.type !== 'individual') {
                    occupation = replacePlaceholders(party.type);
                  }
                  
                  if (occupation) {
                    doc.text('Occupation: ' + occupation, margin, y);
                  } else {
                    doc.text('Occupation: ________________________________________________________________________', margin, y);
                  }
                  y += 16;
                  
                  // Professional signature and date lines
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(10);
                  
                  // Draw actual lines using PDF line drawing
                  doc.setLineWidth(0.5);
                  doc.setDrawColor(0, 0, 0);
                  doc.line(margin + 60, y + 5, margin + 200, y + 5); // Signature line
                  doc.line(margin + 250, y + 5, margin + 350, y + 5); // Date line
                  
                  doc.text('Signature:', margin, y);
                  doc.text('Date:', margin + 220, y);
                  y += 20;
                  
                  // Professional witness section
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(9);
                  doc.text('IN THE PRESENCE OF:', margin + 20, y);
                  y += 15;
                  
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(9);
                  
                  // Witness details with proper lines
                  doc.text('Name: ________________________________', margin + 20, y);
                  y += 12;
                  
                  doc.text('Address: ________________________________', margin + 20, y);
                  y += 12;
                  
                  doc.text('Occupation: ________________________________', margin + 20, y);
                  y += 12;
                  
                  // Witness signature and date with actual lines
                  doc.setLineWidth(0.3);
                  doc.line(margin + 80, y + 5, margin + 180, y + 5); // Witness signature line
                  doc.line(margin + 220, y + 5, margin + 290, y + 5); // Witness date line
                  
                  doc.text('Signature:', margin + 20, y);
                  doc.text('Date:', margin + 200, y);
                  y += 20;
                  
                  doc.text('Tel: ________________________________', margin + 20, y);
                  y += 25;
                  
                  // Add space between parties
                  if (partyIndex < improvedObj.parties.length - 1) {
                    y += 20;
                    
                    // Add a subtle separator line between parties (optional)
                    doc.setLineWidth(0.2);
                    doc.setDrawColor(150, 150, 150);
                    doc.line(margin, y - 10, margin + contentWidth, y - 10);
                    y += 10;
                  }
                });
              } else {
                // Ultimate fallback - generic signature blocks
                const genericParties = ['PARTY 1', 'PARTY 2'];
                
                genericParties.forEach((partyName, partyIndex) => {
                  if (y > 650) {
                    doc.addPage();
                    y = 50;
                  }
                  
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(11);
                  doc.text(`SIGNED, SEALED AND DELIVERED by ${partyName}:`, margin, y);
                  y += 20;
                  
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(10);
                  
                  doc.text('Name: ________________________________', margin, y);
                  y += 16;
                  
                  doc.text('Address: ________________________________', margin, y);
                  y += 16;
                  
                  doc.text('Occupation: ________________________________', margin, y);
                  y += 16;
                  
                  // Signature and date lines
                  doc.setLineWidth(0.5);
                  doc.line(margin + 60, y + 5, margin + 200, y + 5);
                  doc.line(margin + 250, y + 5, margin + 350, y + 5);
                  
                  doc.text('Signature:', margin, y);
                  doc.text('Date:', margin + 220, y);
                  y += 20;
                  
                  // Witness section
                  doc.setFont('helvetica', 'bold');
                  doc.setFontSize(9);
                  doc.text('IN THE PRESENCE OF:', margin + 20, y);
                  y += 15;
                  
                  doc.setFont('helvetica', 'normal');
                  doc.setFontSize(9);
                  
                  doc.text('Name: ________________________________', margin + 20, y);
                  y += 12;
                  
                  doc.text('Address: ________________________________', margin + 20, y);
                  y += 12;
                  
                  doc.text('Occupation: ________________________________', margin + 20, y);
                  y += 12;
                  
                  doc.setLineWidth(0.3);
                  doc.line(margin + 80, y + 5, margin + 180, y + 5);
                  doc.line(margin + 220, y + 5, margin + 290, y + 5);
                  
                  doc.text('Signature:', margin + 20, y);
                  doc.text('Date:', margin + 200, y);
                  y += 20;
                  
                  doc.text('Tel: ________________________________', margin + 20, y);
                  y += 25;
                  
                  if (partyIndex < genericParties.length - 1) {
                    y += 20;
                  }
                });
              }
            }
          }
        });
        
        console.log('Contract Processing Complete - All filtered sections processed');
        
      } else {
        // Fallback: Handle old text-based format (keep existing code)
        console.log('Fallback: Using text-based contract format');
        
        // [Keep your existing fallback code here - unchanged]
        // STEP 1: Extract full contract content from improved_json
        let contractFullContent = '';
        let contractData = null;
        
        if (isPlainText) {
          contractFullContent = improvedObj;
          console.log('Using plain text content, length:', contractFullContent.length);
        } else {
          console.log('Extracting contract content from improved_json');
          contractData = improvedObj;
          
          // Check for new standardized format first (AI returns {text: "standardized contract"})
          if (improvedObj.text && typeof improvedObj.text === 'string') {
            contractFullContent = improvedObj.text;
            console.log('Found standardized text field, length:', contractFullContent.length);
          } else if (improvedObj.content) {
            contractFullContent = improvedObj.content;
            console.log('Found content field, length:', contractFullContent.length);
          } else if (improvedObj.body) {
            contractFullContent = improvedObj.body;
            console.log('Found body field, length:', contractFullContent.length);
          } else {
            // Build from all text fields in improved_json (fallback for old format)
            const textSections = [];
            Object.entries(improvedObj).forEach(([key, value]) => {
              console.log(`Checking contract key ${key}:`, typeof value, value?.length || 'N/A');
              
              if (typeof value === 'string' && value.length > 30) {
                textSections.push(value);
              } else if (typeof value === 'object' && value !== null) {
                Object.values(value).forEach(subVal => {
                  if (typeof subVal === 'string' && subVal.length > 30) {
                    textSections.push(subVal);
                  }
                });
              }
            });
            contractFullContent = textSections.join('\n\n');
            console.log('Built from sections, total length:', contractFullContent.length);
          }
        }
        
        // Fallback if no content found
        if (!contractFullContent.trim()) {
          contractFullContent = typeof revamped === 'string' ? revamped : JSON.stringify(revamped, null, 2);
          console.log('Using fallback content, length:', contractFullContent.length);
        }
        
        console.log('Full contract content length:', contractFullContent.length);
        
        // STEP 2: Parse contract to extract party information for signature table
        function extractContractParties(contractText) {
          console.log('Extracting parties from contract text');
          const parties = [];
          
          // Pattern 1: Freelance/Design Agreement pattern
          // "Creative Studios Pro LLC, a limited liability company ("Company")"
          // "Amanda Foster, an individual ("Designer")"
          const freelancePattern = /^-\s*([^,]+),\s*([^(]+)\s*\("([^"]+)"\)/gm;
          let match;
          while ((match = freelancePattern.exec(contractText)) !== null) {
            parties.push({
              name: match[1].trim(),
              type: match[2].trim(),
              role: match[3].trim()
            });
          }
          
          // Pattern 2: Tenancy Agreement pattern
          // "Mr. Rabiu Salisu Aliyu, herein referred to as the "Landlord,""
          const tenancyPattern = /^-\s*(Mr\.|Mrs\.|Ms\.|Dr\.)?\s*([^,]+),\s*herein referred to as the\s*"([^"]+)"/gm;
          while ((match = tenancyPattern.exec(contractText)) !== null) {
            const title = match[1] || '';
            const name = (title + ' ' + match[2]).trim();
            parties.push({
              name: name,
              type: 'Individual',
              role: match[3].trim()
            });
          }
          
          // Pattern 3: Extract date from "entered into on [date]"
          const datePattern = /entered into on\s+([^,]+),?\s+between/i;
          const dateMatch = contractText.match(datePattern);
          const agreementDate = dateMatch ? dateMatch[1].trim() : new Date().toLocaleDateString();
          
          // Pattern 4: Extract from EXECUTION section
          // "CREATIVE STUDIOS PRO LLC\nBy: Mark Johnson, Creative Director"
          const execPattern = /^([A-Z\s]+(?:LLC|INC|LTD|CORP)?)\s*\nBy:\s*([^,\n]+)(?:,\s*([^\n]+))?/gm;
          while ((match = execPattern.exec(contractText)) !== null) {
            const companyName = match[1].trim();
            const signerName = match[2].trim();
            const signerRole = match[3] ? match[3].trim() : '';
            
            // Check if this company already exists
            const existingParty = parties.find(p => 
              p.name.toUpperCase() === companyName || 
              p.name.toUpperCase().includes(companyName)
            );
            
            if (existingParty) {
              existingParty.signerName = signerName;
              existingParty.signerRole = signerRole;
            }
          }
          
          // Pattern 5: Individual signer pattern
          // "AMANDA FOSTER, DESIGNER\nSignature: Amanda Foster"
          const individualPattern = /^([A-Z\s]+),\s*([A-Z\s]+)\s*\nSignature:\s*([^\n]+)/gm;
          while ((match = individualPattern.exec(contractText)) !== null) {
            const name = match[1].trim();
            const role = match[2].trim();
            const signatureName = match[3].trim();
            
            const existingParty = parties.find(p => 
              p.name.toUpperCase() === name || 
              signatureName.toUpperCase().includes(p.name.toUpperCase())
            );
            
            if (existingParty) {
              existingParty.signerName = signatureName;
              existingParty.signerRole = existingParty.signerRole || role;
            }
          }
          
          console.log('Extracted parties:', parties);
          console.log('Agreement date:', agreementDate);
          
          return { parties, agreementDate };
        }
        
        const { parties: extractedParties, agreementDate } = extractContractParties(contractFullContent);
        
        // STEP 3: Target Template Format - ALL BLACK TEXT
        console.log('Applying target template format - ALL BLACK');
        
        // Split content into paragraphs for processing
        const paragraphs = contractFullContent.split(/\n\s*\n/).filter(p => p.trim().length > 0);
        
        // Extract contract title from first few lines
        const firstLines = contractFullContent.split('\n').slice(0, 5);
        let contractTitle = '';
        let contentStartIndex = 0;
        
        for (let i = 0; i < firstLines.length; i++) {
          const line = firstLines[i].trim();
          if (line.length > 0 && 
              (line.toUpperCase().includes('AGREEMENT') || 
              line.toUpperCase().includes('CONTRACT')) &&
              line.length < 100) {
            contractTitle = line.toUpperCase();
            contentStartIndex = i + 1;
            break;
          }
        }
        
        if (!contractTitle) {
          contractTitle = 'CONTRACT AGREEMENT';
        }
        
        // ====== CONTRACT HEADER (Target Template) ======
        console.log('Rendering contract header - target template');
        
        // Title - BLACK and CENTERED
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0); // BLACK
        
        const titleLines = doc.splitTextToSize(contractTitle, contentWidth);
        titleLines.forEach((line, index) => {
          const lineWidth = doc.getTextWidth(line);
          const xPosition = (pageWidth - lineWidth) / 2;
          doc.text(line, xPosition, y + (index * 20));
        });
        
        y += (titleLines.length * 20) + 30;
        
        // ====== PROCESS CONTRACT SECTIONS ======
        console.log('Processing contract sections - ALL BLACK text');
        
        // Track if we've processed the title to skip duplicate left-aligned titles
        let titleProcessed = false;
        let executionSectionFound = false;
        
        paragraphs.forEach((paragraph, pIndex) => {
          const cleanParagraph = paragraph.trim();
          if (!cleanParagraph) return;
          
          // Skip if this is a duplicate of the main title
          if (!titleProcessed && cleanParagraph.toUpperCase() === contractTitle) {
            titleProcessed = true;
            return;
          }
          
          // Detect section types based on content patterns
          const isMainSectionHeader = cleanParagraph.match(/^\d+\.\s+[A-Z]/);
          const isSubSectionHeader = cleanParagraph.match(/^\d+\.\d+\s/);
          const isIntroduction = cleanParagraph.includes('This Agreement') || cleanParagraph.includes('This Contract') || cleanParagraph.includes('between:');
          const isSignatureSection = cleanParagraph.toUpperCase().includes('EXECUTION') || 
                                    cleanParagraph.toUpperCase().includes('SIGNATURE') || 
                                    cleanParagraph.toUpperCase().includes('IN WITNESS WHEREOF');
          const isListItem = cleanParagraph.match(/^[\-•]\s/) || cleanParagraph.match(/^[a-z]\.\s/);
          
          if (isSignatureSection) {
            executionSectionFound = true;
          }
          
          // Check for page breaks
          if (y > 750) {
            doc.addPage();
            y = 50;
          }
          
          if (isMainSectionHeader) {
            // ====== MAIN SECTION HEADERS - BLACK (NOT BLUE!) ======
            console.log('Rendering main section header:', cleanParagraph.substring(0, 50));
            
            y += 20; // Extra space before main sections
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0); // BLACK (NOT BLUE!)
            
            const headerLines = doc.splitTextToSize(cleanParagraph, contentWidth);
            headerLines.forEach((line) => {
              if (y > 770) {
                doc.addPage();
                y = 50;
              }
              doc.text(line, margin, y);
              y += 16;
            });
            
            y += 8; // Space after header
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            
          } else if (isSubSectionHeader) {
            // ====== SUB-SECTION HEADERS - BLACK (NOT BLUE!) ======
            console.log('Rendering sub-section header (BLACK):', cleanParagraph.substring(0, 50));
            
            y += 12; // Space before sub-sections
            
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0); // BLACK (NOT BLUE!)
            
            const subHeaderLines = doc.splitTextToSize(cleanParagraph, contentWidth);
            subHeaderLines.forEach((line) => {
              if (y > 770) {
                doc.addPage();
                y = 50;
              }
              doc.text(line, margin, y);
              y += 14;
            });
            
            y += 6; // Space after sub-header
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            
          } else if (isIntroduction) {
            // ====== INTRODUCTION PARAGRAPHS - BLACK ======
            console.log('Rendering introduction paragraph');
            
            y += 8;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0); // BLACK
            
            const introLines = doc.splitTextToSize(cleanParagraph, contentWidth);
            introLines.forEach((line, lineIndex) => {
              if (y > 770) {
                doc.addPage();
                y = 50;
              }
              
              const cleanLine = line.trim();
              if (!cleanLine) return;
              
              // Apply justified text for better appearance
              if (lineIndex < introLines.length - 1 && introLines.length > 1) {
                const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                
                if (words.length > 1) {
                  const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                  const spaceToDistribute = contentWidth - totalWordWidth;
                  const spacePerGap = spaceToDistribute / (words.length - 1);
                  const normalSpaceWidth = doc.getTextWidth(' ');
                  
                  if (spacePerGap <= normalSpaceWidth * 2.5 && words.length >= 3) {
                    let currentX = margin;
                    words.forEach((word, wIndex) => {
                      doc.text(word, currentX, y);
                      if (wIndex < words.length - 1) {
                        currentX += doc.getTextWidth(word) + spacePerGap;
                      }
                    });
                  } else {
                    doc.text(cleanLine, margin, y);
                  }
                } else {
                  doc.text(cleanLine, margin, y);
                }
              } else {
                doc.text(cleanLine, margin, y);
              }
              
              y += 14;
            });
            
            y += 8;
            
          } else if (isSignatureSection) {
            // ====== SIGNATURE SECTION - With Extracted Party Data ======
            console.log('Rendering signature section - with extracted party data');
            
            if (y > 650) {
              doc.addPage();
              y = 50;
            }
            
            y += 25;
            
            // Section header - BLACK (NOT BLUE!)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0); // BLACK
            
            // Extract section number from the paragraph if available
            const sectionMatch = cleanParagraph.match(/^(\d+)\.\s+EXECUTION/);
            const sectionNumber = sectionMatch ? sectionMatch[1] : '6';
            
            const headerText = `${sectionNumber}. EXECUTION`;
            doc.text(headerText, margin, y);
            y += 20;
            
            // Execution text - BLACK
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0); // BLACK
            
            if (cleanParagraph.includes('IN WITNESS WHEREOF') || cleanParagraph.includes('parties have executed')) {
              const execText = 'IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.';
              const execLines = doc.splitTextToSize(execText, contentWidth);
              execLines.forEach((line) => {
                doc.text(line, margin, y);
                y += 14;
              });
              y += 20;
            }
            
            // Professional Signature Table with Extracted Data
            console.log('Creating signature table with extracted party data');
            
            // Prepare party data for signature table
            let partyData = [];
            
            if (extractedParties.length > 0) {
              extractedParties.forEach((party) => {
                // Determine the party label based on role
                let partyLabel = party.role;
                if (party.role.toLowerCase().includes('company')) {
                  partyLabel = 'Company';
                } else if (party.role.toLowerCase().includes('designer')) {
                  partyLabel = 'Designer';
                } else if (party.role.toLowerCase().includes('landlord')) {
                  partyLabel = 'Landlord';
                } else if (party.role.toLowerCase().includes('tenant')) {
                  partyLabel = 'Tenant';
                }
                
                partyData.push({
                  party: partyLabel,
                  name: party.name,
                  role: party.signerRole || party.type || '',
                  date: agreementDate
                });
              });
            }
            
            // If no party data found, use generic placeholders
            if (partyData.length === 0) {
              partyData = [
                { party: 'Party 1', name: '[Party 1 Name]', role: '[Role]', date: '[Date]' },
                { party: 'Party 2', name: '[Party 2 Name]', role: '[Role]', date: '[Date]' }
              ];
            }
            
            // Table headers - BLACK
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0); // BLACK headers
            
            const tableStartY = y;
            const col1X = margin;
            const col2X = margin + 120;
            const col3X = margin + 300;
            const col4X = margin + 420;
            
            // Header row
            doc.text('Party', col1X, y);
            doc.text('Name / Role', col2X, y);
            doc.text('Signature', col3X, y);
            doc.text('Date', col4X, y);
            
            y += 15;
            
            // Draw horizontal line under headers
            doc.setLineWidth(0.5);
            doc.setDrawColor(0, 0, 0); // BLACK lines
            doc.line(col1X, y - 5, col4X + 80, y - 5);
            
            // Signature rows - BLACK text with extracted data
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0); // BLACK text
            
            partyData.forEach((party, index) => {
              doc.text(party.party, col1X, y);
              
              // Format name and role
              let nameRoleText = party.name;
              if (party.role && party.role !== 'Individual' && party.role !== party.name) {
                nameRoleText = `${party.name}, ${party.role}`;
              }
              
              // Split long text if needed
              const nameRoleLines = doc.splitTextToSize(nameRoleText, col3X - col2X - 10);
              nameRoleLines.forEach((line, lineIndex) => {
                doc.text(line, col2X, y + (lineIndex * 12));
              });
              
              doc.text(party.date, col4X, y);
              
              // Draw signature line
              doc.setLineWidth(0.3);
              doc.line(col3X, y + 2, col3X + 80, y + 2);
              
              y += Math.max(20, nameRoleLines.length * 12 + 8);
            });
            
            // Draw table borders - BLACK
            doc.setLineWidth(0.5);
            doc.setDrawColor(0, 0, 0); // BLACK borders
            
            // Vertical lines
            doc.line(col1X - 5, tableStartY - 10, col1X - 5, y + 5);
            doc.line(col2X - 10, tableStartY - 10, col2X - 10, y + 5);
            doc.line(col3X - 10, tableStartY - 10, col3X - 10, y + 5);
            doc.line(col4X - 10, tableStartY - 10, col4X - 10, y + 5);
            doc.line(col4X + 85, tableStartY - 10, col4X + 85, y + 5);
            
            // Horizontal lines
            doc.line(col1X - 5, tableStartY - 10, col4X + 85, tableStartY - 10);
            doc.line(col1X - 5, y + 5, col4X + 85, y + 5);
            
            y += 15;
            
          } else if (isListItem) {
            // ====== LIST ITEMS - BLACK ======
            console.log('Rendering list item');
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0); // BLACK
            
            const listLines = doc.splitTextToSize(cleanParagraph, contentWidth - 20);
            listLines.forEach((line, lineIndex) => {
              if (y > 770) {
                doc.addPage();
                y = 50;
              }
              
              const indent = lineIndex === 0 ? margin : margin + 10;
              doc.text(line.trim(), indent, y);
              y += 13;
            });
            
            y += 4;
            
          } else {
            // ====== REGULAR CONTENT PARAGRAPHS - BLACK ======
            console.log('Rendering regular content paragraph');
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0); // BLACK
            
            const contentLines = doc.splitTextToSize(cleanParagraph, contentWidth);
            
            contentLines.forEach((line, lineIndex) => {
              if (y > 770) {
                doc.addPage();
                y = 50;
              }
              
              const cleanLine = line.trim();
              if (!cleanLine) return;
              
              // Apply justified text
              if (lineIndex < contentLines.length - 1 && contentLines.length > 1) {
                const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                
                if (words.length > 1) {
                  const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                  const spaceToDistribute = contentWidth - totalWordWidth;
                  const spacePerGap = spaceToDistribute / (words.length - 1);
                  const normalSpaceWidth = doc.getTextWidth(' ');
                  
                  if (spacePerGap <= normalSpaceWidth * 2.5 && words.length >= 3) {
                    let currentX = margin;
                    words.forEach((word, wIndex) => {
                      doc.text(word, currentX, y);
                      if (wIndex < words.length - 1) {
                        currentX += doc.getTextWidth(word) + spacePerGap;
                      }
                    });
                  } else {
                    doc.text(cleanLine, margin, y);
                  }
                } else {
                  doc.text(cleanLine, margin, y);
                }
              } else {
                doc.text(cleanLine, margin, y);
              }
              
              y += 14;
            });
            
            if (pIndex < paragraphs.length - 1) {
              y += 12;
            }
          }
        });
        
        console.log('Contract Processing Complete - Target Template Format Applied');
      }
      
      console.log('Contract Processing Complete - Structured Format');
    }
    else if (docType.toLowerCase() === 'cv') {
      console.log('CV Processing Started - Creating Professional Single-Column Layout');
      
      let cvData;
      let purpose = 'work'; // default
      
      // Handle both old text format and new structured format
      if (isPlainText) {
        console.log('CV is in plain text format - this should not happen with new structure');
        // Fallback to generic formatting
        addSectionHeader(`Enhanced ${getDocTypeTitle(docType)}`);
        addContent(improvedObj);
        return;
      } else {
        // Check if we have the new structured format
        if (improvedObj.purpose && improvedObj.full_name && improvedObj.experience) {
          console.log('Using NEW structured CV format');
          cvData = improvedObj;
          purpose = improvedObj.purpose || 'work';
        } else if (improvedObj.text) {
          console.log('CV has text field - using old format fallback');
          // Old format fallback
          addSectionHeader(`Enhanced ${getDocTypeTitle(docType)}`);
          addContent(improvedObj.text);
          return;
        } else {
          console.log('CV format not recognized - using generic fallback');
          addSectionHeader(`Enhanced ${getDocTypeTitle(docType)}`);
          addContent(renderJsonAsText(improvedObj));
          return;
        }
      }
      
      // Get purpose from context if available
      if (revampData?.context_json?.purpose) {
        purpose = revampData.context_json.purpose.toLowerCase();
      }
      
      console.log('CV Purpose:', purpose);
      console.log('CV Data:', cvData);
      
      // ====== PROFESSIONAL SINGLE-COLUMN CV LAYOUT ======
      // Reset margins for professional layout
      const cvMargin = 40;
      const cvContentWidth = pageWidth - (2 * cvMargin);
      
      // ====== CV HEADER - CENTERED NAME ======
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      doc.setTextColor(0, 0, 0); // Black
      
      const fullName = cvData.full_name || 'Your Name';
      const nameLines = doc.splitTextToSize(fullName.toUpperCase(), cvContentWidth);
      nameLines.forEach((line, index) => {
        const lineWidth = doc.getTextWidth(line);
        const xPosition = (pageWidth - lineWidth) / 2;
        doc.text(line, xPosition, y + (index * 30));
      });
              y += (nameLines.length * 25);
        
        // ====== CONTACT INFORMATION - CENTERED WITH PROPER WRAPPING ======
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        
        // First line: email | phone
        const firstLineContact = [];
        if (cvData.email) firstLineContact.push(cvData.email);
        if (cvData.phone) firstLineContact.push(cvData.phone);
        
        if (firstLineContact.length > 0) {
          const firstContactLine = firstLineContact.join(' | ');
          const firstContactWidth = doc.getTextWidth(firstContactLine);
          const firstContactXPosition = (pageWidth - firstContactWidth) / 2;
          doc.text(firstContactLine, firstContactXPosition, y);
          y += 12;
        }
        
        // Second line: address (if available)
        if (cvData.address) {
          const addressWidth = doc.getTextWidth(cvData.address);
          const addressXPosition = (pageWidth - addressWidth) / 2;
          doc.text(cvData.address, addressXPosition, y);
          y += 12;
        }
        
        // Third line: LinkedIn (if available)
        if (cvData.linkedin) {
          const linkedinWidth = doc.getTextWidth(cvData.linkedin);
          const linkedinXPosition = (pageWidth - linkedinWidth) / 2;
          doc.text(cvData.linkedin, linkedinXPosition, y);
          y += 12;
        }
        
        // Remove professional title/subtitle completely as requested
        
        y += 20; // Extra space after header
      
      // ====== CAREER OBJECTIVE/PROFESSIONAL SUMMARY ======
      if (cvData.summary) {
        // Section header with blue color like Image 1
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(37, 99, 235); // Blue color for headers
        doc.text('CAREER STRATEGIC PURPOSE', cvMargin, y);
        y += 18;
        
        // Content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        
        const summaryLines = doc.splitTextToSize(cvData.summary, cvContentWidth);
        summaryLines.forEach((line, lineIndex) => {
          if (y > 770) {
            doc.addPage();
            y = 50;
          }
          
          const cleanLine = line.trim();
          if (!cleanLine) return;
          
          // Apply justification for better appearance
          if (lineIndex < summaryLines.length - 1 && summaryLines.length > 1) {
            const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
            
            if (words.length > 1) {
              const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
              const spaceToDistribute = cvContentWidth - totalWordWidth;
              const spacePerGap = spaceToDistribute / (words.length - 1);
              const normalSpaceWidth = doc.getTextWidth(' ');
              
              if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                let currentX = cvMargin;
                words.forEach((word, wIndex) => {
                  doc.text(word, currentX, y);
                  if (wIndex < words.length - 1) {
                    currentX += doc.getTextWidth(word) + spacePerGap;
                  }
                });
              } else {
                doc.text(cleanLine, cvMargin, y);
              }
            } else {
              doc.text(cleanLine, cvMargin, y);
            }
          } else {
            doc.text(cleanLine, cvMargin, y);
          }
          y += 12;
        });
        y += 15;
      }
      
      // ====== PERSONAL BIODATA SECTION ======
      if (cvData.personal_info && Object.keys(cvData.personal_info).length > 0) {
        // Section header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(37, 99, 235); // Blue color
        doc.text('PERSONAL BIODATA', cvMargin, y);
        y += 18;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        
        // Create two-column layout for personal info
        const personalInfoItems = [];
        if (cvData.personal_info.date_of_birth) personalInfoItems.push(['Date of Birth:', cvData.personal_info.date_of_birth]);
        if (cvData.personal_info.gender) personalInfoItems.push(['Gender:', cvData.personal_info.gender]);
        if (cvData.personal_info.nationality) personalInfoItems.push(['Nationality:', cvData.personal_info.nationality]);
        if (cvData.personal_info.interests) personalInfoItems.push(['Interests:', cvData.personal_info.interests]);
        
        // Add preferred call name if available
        if (cvData.full_name) {
          const firstName = cvData.full_name.split(' ')[0];
          personalInfoItems.push(['Preferred Call Name:', firstName]);
        }
        
        personalInfoItems.forEach(([label, value]) => {
          if (y > 750) {
            doc.addPage();
            y = 50;
          }
          
          // Create two-column format like Image 1
          doc.setFont('helvetica', 'bold');
          doc.text(label, cvMargin, y);
          
          doc.setFont('helvetica', 'normal');
          doc.text(value, cvMargin + 120, y);
          y += 14;
        });
        
        y += 15;
      }
      
      // ====== WORK EXPERIENCE ======
      if (cvData.experience && cvData.experience.length > 0) {
        // Section header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(37, 99, 235); // Blue color
        doc.text('WORK EXPERIENCE', cvMargin, y);
        y += 18;
        
        cvData.experience.forEach((job, jobIndex) => {
          if (y > 700) {
            doc.addPage();
            y = 50;
          }
          
          // Company and role with location
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          
          const jobTitle = job.job_title || job.title || 'Job Title';
          const company = job.company || 'Company';
          const location = job.location || '';
          
          doc.text(`${company}: ${jobTitle}`, cvMargin, y);
          y += 14;
          
          // Location and duration with proper icons (like Image 2)
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          
          // Location and duration with simple symbols
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          
          // Location with simple symbol
          doc.setTextColor(220, 20, 60); // Red color
          doc.text('>', cvMargin, y); // Simple arrow for location
          
          doc.setTextColor(0, 0, 0);
          doc.text(`Location: ${location || 'Not specified'}`, cvMargin + 15, y);
          y += 14;
          
          // Duration with simple symbol
          doc.setTextColor(220, 20, 60); // Red color
          doc.text('<', cvMargin, y); // Simple arrow for duration
          
          doc.setTextColor(0, 0, 0);
          const duration = job.duration || 'Duration not specified';
          doc.text(`Duration: ${duration}`, cvMargin + 15, y);
          y += 16;
          
          // Job Description and Key Achievements header
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.setTextColor(220, 20, 60); // Red color like Image 1
          doc.text('Job Description and Key Achievements', cvMargin, y);
          y += 14;
          
          // Job achievements/description - Different format based on purpose
          if (job.achievements) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            
            if (purpose === 'work') {
              // WORK PURPOSE: Use bullet points (Image 1 style)
              console.log('Formatting experience for WORK purpose with bullet points');
              
              let achievementItems = [];
              if (Array.isArray(job.achievements)) {
                achievementItems = job.achievements;
              } else {
                // Split paragraph into sentences for bullet points
                achievementItems = job.achievements.split(/[.!?]\s+/).filter(s => s.trim());
              }
              
              achievementItems.forEach((achievement) => {
                if (y > 760) {
                  doc.addPage();
                  y = 50;
                }
                
                // Clean up the achievement text
                let cleanAchievement = achievement.trim();
                if (!cleanAchievement.endsWith('.') && !cleanAchievement.endsWith('!') && !cleanAchievement.endsWith('?')) {
                  cleanAchievement += '.';
                }
                
                // ASCII-safe bullet point
                doc.text('•', cvMargin, y);
                
                // Achievement text with proper wrapping
                const bulletText = cleanAchievement;
                const bulletLines = doc.splitTextToSize(bulletText, cvContentWidth - 15);
                
                bulletLines.forEach((line, lineIndex) => {
                  if (y > 770) {
                    doc.addPage();
                    y = 50;
                  }
                  
                  const indent = lineIndex === 0 ? cvMargin + 15 : cvMargin + 15;
                  doc.text(line.trim(), indent, y);
                  y += 12;
                });
                
                y += 2; // Small space between bullet points
              });
              
            } else {
              // STUDY PURPOSE: Use paragraph format (Image 2 style)
              console.log('Formatting experience for STUDY purpose with paragraph');
              
              let achievementText = '';
              if (Array.isArray(job.achievements)) {
                achievementText = job.achievements.join('. ') + '.';
              } else {
                achievementText = job.achievements;
              }
              
              const achievementLines = doc.splitTextToSize(achievementText, cvContentWidth);
              achievementLines.forEach((line, lineIndex) => {
                if (y > 770) {
                  doc.addPage();
                  y = 50;
                }
                
                const cleanLine = line.trim();
                if (!cleanLine) return;
                
                // Apply justification for paragraph format
                if (lineIndex < achievementLines.length - 1 && achievementLines.length > 1) {
                  const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                  
                  if (words.length > 1) {
                    const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                    const spaceToDistribute = cvContentWidth - totalWordWidth;
                    const spacePerGap = spaceToDistribute / (words.length - 1);
                    const normalSpaceWidth = doc.getTextWidth(' ');
                    
                    if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                      let currentX = cvMargin;
                      words.forEach((word, wIndex) => {
                        doc.text(word, currentX, y);
                        if (wIndex < words.length - 1) {
                          currentX += doc.getTextWidth(word) + spacePerGap;
                        }
                      });
                    } else {
                      doc.text(cleanLine, cvMargin, y);
                    }
                  } else {
                    doc.text(cleanLine, cvMargin, y);
                  }
                } else {
                  doc.text(cleanLine, cvMargin, y);
                }
                y += 14;
              });
            }
          }
          
          // Add space between jobs
          if (jobIndex < cvData.experience.length - 1) {
            y += 15;
          }
        });
        y += 18;
      }
      
      // ====== EDUCATION AND TRAINING ======
      if (cvData.education && cvData.education.length > 0) {
        if (y > 650) {
          doc.addPage();
          y = 50;
        }
        
        // Section header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(37, 99, 235); // Blue color
        doc.text('EDUCATION AND TRAINING', cvMargin, y);
        y += 18;
        
        cvData.education.forEach((edu, eduIndex) => {
          if (y > 720) {
            doc.addPage();
            y = 50;
          }
          
          // Degree and institution
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);
          
          const degree = edu.degree || 'Degree';
          const institution = edu.institution || 'Institution';
          const duration = edu.duration || '';
          
          doc.text(`${degree} (${institution})`, cvMargin, y);
          y += 14;
          
          // Institution name and graduation date
          doc.text(`${institution} | ${duration}`, cvMargin, y);
          y += 14;
          
          // Details if available
          if (edu.details && edu.details.trim()) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            doc.text(edu.details, cvMargin, y);
            y += 14;
          }
          
          // Add space between education entries
          if (eduIndex < cvData.education.length - 1) {
            y += 8;
          }
        });
        y += 18;
      }
      
              // ====== SKILLS SECTION ======
        if (cvData.skills && cvData.skills.length > 0) {
          if (y > 650) {
            doc.addPage();
            y = 50;
          }
          
          // Section header
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.setTextColor(37, 99, 235); // Blue color
          doc.text('SKILLS', cvMargin, y);
          y += 25;
          
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);
          
          if (purpose === 'work') {
            // WORK PURPOSE: Two-column layout with wrapping to avoid overlap
            const columnGap = 10;
            const bulletIndent = 8;
            const lineHeight = 12;
            const columnWidth = (cvContentWidth / 2) - columnGap - bulletIndent;

            for (let i = 0; i < cvData.skills.length; i += 2) {
              if (y > 760) {
                doc.addPage();
                y = 50;
              }

              const skill1 = String(cvData.skills[i] || '');
              const skill2 = String(cvData.skills[i + 1] || '');

              const lines1 = doc.splitTextToSize(skill1, columnWidth);
              const lines2 = skill2 ? doc.splitTextToSize(skill2, columnWidth) : [];
              const numLines = Math.max(lines1.length || 1, lines2.length || 0);

              // Left column (with wrapping)
              for (let li = 0; li < lines1.length; li++) {
                const yLine = y + li * lineHeight;
                if (yLine > 770) { doc.addPage(); y = 50; li = -1; continue; }
                if (li === 0) doc.text('•', cvMargin, yLine);
                doc.text(lines1[li], cvMargin + bulletIndent, yLine);
              }

              // Right column (with wrapping)
              if (skill2) {
                const col2X = cvMargin + (cvContentWidth / 2);
                for (let ri = 0; ri < lines2.length; ri++) {
                  const yLine = y + ri * lineHeight;
                  if (yLine > 770) { doc.addPage(); y = 50; ri = -1; continue; }
                  if (ri === 0) doc.text('•', col2X, yLine);
                  doc.text(lines2[ri], col2X + bulletIndent, yLine);
                }
              }

              // Advance by the tallest column
              y += numLines * lineHeight + 2;
            }
          } else {
            // STUDY PURPOSE: Use horizontal paragraph format for skills
            const skillsText = cvData.skills.join(', ') + '.';
            const skillsLines = doc.splitTextToSize(skillsText, cvContentWidth);
            
            skillsLines.forEach((line, lineIndex) => {
              if (y > 770) {
                doc.addPage();
                y = 50;
              }
              
              const cleanLine = line.trim();
              if (!cleanLine) return;
              
              // Apply justification for paragraph format
              if (lineIndex < skillsLines.length - 1 && skillsLines.length > 1) {
                const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
                
                if (words.length > 1) {
                  const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                  const spaceToDistribute = cvContentWidth - totalWordWidth;
                  const spacePerGap = spaceToDistribute / (words.length - 1);
                  const normalSpaceWidth = doc.getTextWidth(' ');
                  
                  if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                    let currentX = cvMargin;
                    words.forEach((word, wIndex) => {
                      doc.text(word, currentX, y);
                      if (wIndex < words.length - 1) {
                        currentX += doc.getTextWidth(word) + spacePerGap;
                      }
                    });
                  } else {
                    doc.text(cleanLine, cvMargin, y);
                  }
                } else {
                  doc.text(cleanLine, cvMargin, y);
                }
              } else {
                doc.text(cleanLine, cvMargin, y);
              }
              y += 12;
            });
          }
          y += 18;
        }
      
      // ====== CERTIFICATIONS SECTION ======
      if (cvData.certifications && cvData.certifications.length > 0) {
        if (y > 650) {
          doc.addPage();
          y = 50;
        }
        
        // Section header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(37, 99, 235); // Blue color
        doc.text('CERTIFICATIONS', cvMargin, y);
        y += 18;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        
        if (purpose === 'work') {
          // WORK PURPOSE: Use bullet points for certifications
          cvData.certifications.forEach((cert) => {
            if (y > 760) {
              doc.addPage();
              y = 50;
            }
            
            // ASCII-safe bullet point
            doc.text('•', cvMargin, y);
            
            // Certification text
            let certText = cert.name;
            if (cert.issuer) certText += ` (${cert.issuer})`;
            if (cert.date) certText += ` - ${cert.date}`;
            
            const certLines = doc.splitTextToSize(certText, cvContentWidth - 15);
            certLines.forEach((line, lineIndex) => {
              if (y > 770) {
                doc.addPage();
                y = 50;
              }
              
              const indent = lineIndex === 0 ? cvMargin + 15 : cvMargin + 15;
              doc.text(line.trim(), indent, y);
              y += 14;
            });
            
            y += 4; // Small space between certifications
          });
        } else {
          // STUDY PURPOSE: Use paragraph format for certifications
          const certTexts = cvData.certifications.map(cert => {
            let certText = cert.name;
            if (cert.issuer) certText += ` from ${cert.issuer}`;
            if (cert.date) certText += ` (${cert.date})`;
            return certText;
          });
          
          const certificationsText = certTexts.join(', ') + '.';
          const certLines = doc.splitTextToSize(certificationsText, cvContentWidth);
          
          certLines.forEach((line, lineIndex) => {
            if (y > 770) {
              doc.addPage();
              y = 50;
            }
            
            const cleanLine = line.trim();
            if (!cleanLine) return;
            
            // Apply justification for paragraph format
            if (lineIndex < certLines.length - 1 && certLines.length > 1) {
              const words = cleanLine.split(/\s+/).filter(w => w.length > 0);
              
              if (words.length > 1) {
                const totalWordWidth = words.reduce((sum, word) => sum + doc.getTextWidth(word), 0);
                const spaceToDistribute = cvContentWidth - totalWordWidth;
                const spacePerGap = spaceToDistribute / (words.length - 1);
                const normalSpaceWidth = doc.getTextWidth(' ');
                
                if (spacePerGap <= normalSpaceWidth * 3 && words.length >= 4) {
                  let currentX = cvMargin;
                  words.forEach((word, wIndex) => {
                    doc.text(word, currentX, y);
                    if (wIndex < words.length - 1) {
                      currentX += doc.getTextWidth(word) + spacePerGap;
                    }
                  });
                } else {
                  doc.text(cleanLine, cvMargin, y);
                }
              } else {
                doc.text(cleanLine, cvMargin, y);
              }
            } else {
              doc.text(cleanLine, cvMargin, y);
            }
            y += 14;
          });
        }
        y += 25;
      }
      
      // ====== REFERENCES ======
      if (y > 750) {
        doc.addPage();
        y = 50;
      }
      
      // Section header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(37, 99, 235); // Blue color
      doc.text('REFERENCES', cvMargin, y);
      y += 25;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text('Available on request', cvMargin, y);
      
      console.log('CV Processing Complete - Professional Single-Column Layout Created');
    }
    else {
      // Generic formatting
      if (isPlainText) {
        addSectionHeader(`${getDocTypeTitle(docType)}`);
        addContent(improvedObj);
      } else {
        addSectionHeader(`${getDocTypeTitle(docType)}`);
        addContent(renderJsonAsText(improvedObj));
      }
    }
    // Save the PDF
    
    const fileName = `${docType}-enhanced-${id}.pdf`;
    doc.save(fileName);
    toast.success('PDF downloaded successfully!');
    
    console.log('PDF saved as:', fileName);
  };

  // DOCX handler that matches PDF template designs exactly
  const handleDownloadDocx = async () => {
    if (!revamped) {
      toast.error('No revamped content available for download.');
      return;
    }

    let improvedObj;
    let isPlainText = false;
    
    try {
      improvedObj = typeof revamped === 'string' ? JSON.parse(revamped) : revamped;
      console.log('Parsed JSON object for DOCX:', improvedObj);
    } catch {
      isPlainText = true;
      improvedObj = revamped;
      console.log('Using plain text for DOCX:', typeof improvedObj);
    }

    try {
      // Initialize document with proper metadata to avoid 'creator' error
      const doc = new DocxDocument({
        creator: "DocxsAi",
        title: `Enhanced ${getDocTypeTitle(docType)}`,
        description: `Enhanced ${docType} document`,
        sections: []
      });
      const children = [];
      
      if (docType.toLowerCase() === 'sop') {
        console.log('SOP DOCX Processing Started - Matching PDF Template');
        
        // Extract content (same logic as PDF)
        let sopFullContent = '';
        if (isPlainText) {
          sopFullContent = improvedObj;
        } else {
          if (improvedObj.text) {
            sopFullContent = improvedObj.text;
          } else if (improvedObj.content) {
            sopFullContent = improvedObj.content;
          } else if (improvedObj.body) {
            sopFullContent = improvedObj.body;
          } else {
            const textSections = [];
            Object.entries(improvedObj).forEach(([key, value]) => {
              if (typeof value === 'string' && value.length > 30) {
                textSections.push(value);
              } else if (typeof value === 'object' && value !== null) {
                Object.values(value).forEach(subVal => {
                  if (typeof subVal === 'string' && subVal.length > 30) {
                    textSections.push(subVal);
                  }
                });
              }
            });
            sopFullContent = textSections.join('\n\n');
          }
        }

        if (!sopFullContent.trim()) {
          sopFullContent = typeof revamped === 'string' ? revamped : JSON.stringify(revamped, null, 2);
        }

        // Check if AI provided standardized format (same logic as PDF)
        const hasStandardizedHeader = sopFullContent.includes('STATEMENT OF PURPOSE FOR A');
        
        if (hasStandardizedHeader) {
          // AI provided standardized format - parse and use it
          const lines = sopFullContent.split('\n').filter(line => line.trim());
          let headerProcessed = false;
          let nameProcessed = false;
          let bodyStartIndex = 0;
          
          // Find where body content starts
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            
            if (line.includes('STATEMENT OF PURPOSE FOR A') && !headerProcessed) {
              headerProcessed = true;
              continue;
            }
            
            if (headerProcessed && !nameProcessed && line.length < 50 && line === line.toUpperCase()) {
              nameProcessed = true;
              continue;
            }
            
            if (headerProcessed && (nameProcessed || line.length > 50)) {
              bodyStartIndex = i;
              break;
            }
          }
          
          const headerLine = lines.find(line => line.includes('STATEMENT OF PURPOSE FOR A')) || '';
          const nameLine = lines.find((line, index) => {
            const trimmed = line.trim();
            return index > 0 && 
                  index < bodyStartIndex && 
                  trimmed.length < 50 && 
                  trimmed === trimmed.toUpperCase() &&
                  !trimmed.includes('STATEMENT OF PURPOSE');
          }) || '';
          
          // HEADER - Red, Bold, Centered (matching PDF)
          if (headerLine) {
            children.push(new Paragraph({
              children: [new TextRun({ 
                text: headerLine, 
                bold: true, 
                size: 28,
                color: "DC143C" // Crimson red like PDF
              })],
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 400 }
            }));
          }
          
          // "BY" - Centered, Black, Normal (matching PDF)
          children.push(new Paragraph({
            children: [new TextRun({ text: 'BY', size: 24 })],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 }
          }));
          
          // NAME - Centered, Black, Bold (matching PDF)
          if (nameLine) {
            children.push(new Paragraph({
              children: [new TextRun({ text: nameLine, bold: true, size: 24 })],
              alignment: AlignmentType.CENTER,
              spacing: { after: 600 }
            }));
          }
          
          // BODY - Black, Normal, Justified (matching PDF with proper paragraph breaks)
          const bodyLines = lines.slice(bodyStartIndex);
          const bodyContent = bodyLines.join('\n');
          
          // Split into paragraphs - handle both double newlines and natural paragraph breaks
          let paragraphs = bodyContent.split(/\n\s*\n/).filter(p => p.trim().length > 0);
          
          // If we don't get enough paragraphs from double newlines, try single newlines for longer content
          if (paragraphs.length === 1 && bodyContent.length > 500) {
            // Split on single newlines but group sentences that belong together
            const lines = bodyContent.split('\n').filter(line => line.trim());
            paragraphs = [];
            let currentParagraph = '';
            
            lines.forEach((line) => {
              const trimmedLine = line.trim();
              if (!trimmedLine) return;
              
              // If this line looks like a new paragraph start or is long enough
              if (trimmedLine.length > 100 || currentParagraph.length > 400) {
                if (currentParagraph.trim()) {
                  paragraphs.push(currentParagraph.trim());
                }
                currentParagraph = trimmedLine;
              } else {
                currentParagraph += (currentParagraph ? ' ' : '') + trimmedLine;
              }
            });
            
            // Add the last paragraph
            if (currentParagraph.trim()) {
              paragraphs.push(currentParagraph.trim());
            }
          }
          
          // Create DOCX paragraphs with proper spacing
          paragraphs.forEach((paragraph, pIndex) => {
            const cleanParagraph = paragraph.trim();
            if (!cleanParagraph) return;
            
            children.push(new Paragraph({
              children: [new TextRun({ text: cleanParagraph, size: 24 })],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: pIndex < paragraphs.length - 1 ? 600 : 300 // More space between paragraphs, less after last
              }
            }));
          });
          
        } else {
          // Build manual header (same logic as PDF)
          let courseName = '';
          let institutionName = '';
          let programType = 'MASTER\'S PROGRAM';
          let candidateName = '';
          
          if (revampData && revampData.context_json) {
            const context = revampData.context_json;
            courseName = context.course_of_study || context.program || context.field || '';
            institutionName = context.institution || context.university || '';
            
            const contextProgramType = context.program_type || '';
            if (contextProgramType) {
              const lowerType = contextProgramType.toLowerCase();
              if (lowerType.includes('phd') || lowerType.includes('doctorate')) {
                programType = 'PHD PROGRAM';
              } else if (lowerType.includes('bachelor')) {
                programType = 'BACHELOR\'S PROGRAM';
              } else if (lowerType.includes('mba')) {
                programType = 'MBA PROGRAM';
              } else if (lowerType.includes('master')) {
                programType = 'MASTER\'S PROGRAM';
              }
            }
          }
          
          if (!isPlainText) {
            candidateName = improvedObj.candidate_name || improvedObj.name || '';
          }
          
          // MANUAL HEADER - Red, Bold, Centered
          let mainTitle = '';
          if (courseName && institutionName) {
            mainTitle = `STATEMENT OF PURPOSE FOR A ${programType} IN ${courseName.toUpperCase()} AT ${institutionName.toUpperCase()}`;
          } else if (courseName) {
            mainTitle = `STATEMENT OF PURPOSE FOR A ${programType} IN ${courseName.toUpperCase()}`;
          } else {
            mainTitle = `STATEMENT OF PURPOSE FOR A ${programType}`;
          }
          
          children.push(new Paragraph({
            children: [new TextRun({ 
              text: mainTitle, 
              bold: true, 
              size: 28,
              color: "DC143C" // Crimson red
            })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 400 }
          }));
          
          // BY
          children.push(new Paragraph({
            children: [new TextRun({ text: 'BY', size: 24 })],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 }
          }));
          
          // NAME
          if (candidateName) {
            children.push(new Paragraph({
              children: [new TextRun({ text: candidateName.toUpperCase(), bold: true, size: 24 })],
              alignment: AlignmentType.CENTER,
              spacing: { after: 600 }
            }));
          }
          
          // BODY - Justified paragraphs with proper breaks
          let paragraphs = sopFullContent.split(/\n\s*\n/).filter(p => p.trim().length > 0);
          
          // Improve paragraph detection for better formatting
          if (paragraphs.length === 1 && sopFullContent.length > 500) {
            // Split on single newlines but group sentences that belong together
            const lines = sopFullContent.split('\n').filter(line => line.trim());
            paragraphs = [];
            let currentParagraph = '';
            
            lines.forEach((line) => {
              const trimmedLine = line.trim();
              if (!trimmedLine) return;
              
              // If this line looks like a new paragraph start or current paragraph is getting long
              if (trimmedLine.length > 100 || currentParagraph.length > 400) {
                if (currentParagraph.trim()) {
                  paragraphs.push(currentParagraph.trim());
                }
                currentParagraph = trimmedLine;
              } else {
                currentParagraph += (currentParagraph ? ' ' : '') + trimmedLine;
              }
            });
            
            // Add the last paragraph
            if (currentParagraph.trim()) {
              paragraphs.push(currentParagraph.trim());
            }
          }
          
          paragraphs.forEach((paragraph, pIndex) => {
            const cleanParagraph = paragraph.trim();
            if (!cleanParagraph) return;
            
            children.push(new Paragraph({
              children: [new TextRun({ text: cleanParagraph, size: 24 })],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { 
                after: pIndex < paragraphs.length - 1 ? 600 : 300 // More space between paragraphs
              }
            }));
          });
        }

      } else if (docType.toLowerCase() === 'contract') {
        console.log('Contract DOCX Processing Started - Matching PDF Template');

        // Check for structured format (same logic as PDF)
        const isStructured = improvedObj.title && improvedObj.sections && improvedObj.parties;
        
        if (isStructured) {
          console.log('Using structured contract JSON format for DOCX');
          
          // Filter sections (same logic as PDF)
          const hasContent = (content) => content && content.trim() && content.trim() !== '{}';
          
          const subsectionHasContent = (subsection) => {
            return (hasContent(subsection.content)) || 
                  (subsection.numbered_items && Array.isArray(subsection.numbered_items) && subsection.numbered_items.length > 0) ||
                  (subsection.table && subsection.table.headers && subsection.table.rows);
          };
          
          const sectionHasContent = (section) => {
            const hasDirectContent = hasContent(section.content);
            const hasValidSubsections = section.subsections && 
                                        Array.isArray(section.subsections) && 
                                        section.subsections.some(subsectionHasContent);
            return hasDirectContent || hasValidSubsections;
          };
          
          const filteredSections = improvedObj.sections
            .filter(sectionHasContent)
            .map((section, sectionIndex) => {
              const filteredSubsections = section.subsections ? 
                section.subsections.filter(subsectionHasContent) : [];
              
              const renumberedSubsections = filteredSubsections.map((subsection, subIndex) => ({
                ...subsection,
                number: `${sectionIndex + 1}.${subIndex + 1}`,
                originalNumber: subsection.number
              }));
              
              return {
                ...section,
                number: (sectionIndex + 1).toString(),
                originalNumber: section.number,
                subsections: renumberedSubsections
              };
            });
          
          // CONTRACT HEADER - Black, Bold, Centered (matching PDF)
          const title = replacePlaceholders(improvedObj.title) || 'CONTRACT AGREEMENT';
          children.push(new Paragraph({
            children: [new TextRun({ text: title, bold: true, size: 32 })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 600 }
          }));
          
          // Process filtered sections (matching PDF logic)
          filteredSections.forEach((section) => {
            // Main section header - BLACK (not blue, as you settled for black)
            if (section.title) {
              children.push(new Paragraph({
                children: [new TextRun({ 
                  text: `${section.number}. ${section.title}`, 
                  bold: true, 
                  size: 24
                })],
                spacing: { before: 400, after: 200 }
              }));
            }
            
            // Section content - Justified
            if (section.content && section.content.trim()) {
              children.push(new Paragraph({
                children: [new TextRun({ text: formatContentWithLists(section.content), size: 22 })],
                alignment: AlignmentType.JUSTIFIED,
                spacing: { after: 200 }
              }));
            }
            
            // Process subsections
            if (section.subsections && section.subsections.length > 0) {
              section.subsections.forEach((subsection) => {
                // Subsection header - BLACK
                if (subsection.title && subsection.title.trim()) {
                  children.push(new Paragraph({
                    children: [new TextRun({ 
                      text: `${subsection.number} ${replacePlaceholders(subsection.title)}`, 
                      bold: true, 
                      size: 22
                    })],
                    spacing: { before: 200, after: 150 }
                  }));
                }
                
                // Handle numbered items
                if (subsection.numbered_items && Array.isArray(subsection.numbered_items) && subsection.numbered_items.length > 0) {
                  subsection.numbered_items.forEach((item) => {
                    if (item && item.content && item.number) {
                      const itemText = `${item.number}. ${replacePlaceholders(item.content)}`;
                      children.push(new Paragraph({
                        children: [new TextRun({ text: itemText, size: 22 })],
                        alignment: AlignmentType.JUSTIFIED,
                        spacing: { after: 150 }
                      }));
                    }
                  });
                } else if (subsection.content && subsection.content.trim()) {
                  // Skip signature content as it will be handled specially
                  if (!(subsection.title && subsection.title.toLowerCase().includes('signature'))) {
                    children.push(new Paragraph({
                      children: [new TextRun({ text: formatContentWithLists(subsection.content), size: 22 })],
                      alignment: AlignmentType.JUSTIFIED,
                      spacing: { after: 150 }
                    }));
                  }
                }
              });
            }
            
            // Special handling for EXECUTION section (matching PDF)
            if (section.title && section.title.toUpperCase() === "EXECUTION") {
              // Add witness statement
              children.push(new Paragraph({
                children: [new TextRun({ 
                  text: 'IN WITNESS WHEREOF the Parties have hereunto set their respective hands and seals the day and year first mentioned.', 
                  size: 22 
                })],
                alignment: AlignmentType.JUSTIFIED,
                spacing: { before: 400, after: 400 }
              }));
              
              // Professional signature blocks (matching PDF layout)
              if (improvedObj.parties && improvedObj.parties.length > 0) {
                improvedObj.parties.forEach((party) => {
                  // Party signature header
                  children.push(new Paragraph({
                    children: [new TextRun({ 
                      text: `SIGNED, SEALED AND DELIVERED by the within named ${party.role.toUpperCase()}:`, 
                      bold: true, 
                      size: 22 
                    })],
                    spacing: { before: 300, after: 200 }
                  }));
                  
                  // Name field
                  let partyName = replacePlaceholders(party.name);
                  if (party.signerName && party.signerName !== party.name) {
                    partyName = replacePlaceholders(party.signerName);
                  }
                  children.push(new Paragraph({
                    children: [new TextRun({ text: `Name: ${partyName}`, size: 20 })],
                    spacing: { after: 150 }
                  }));
                  
                  // Address field
                  if (party.address) {
                    children.push(new Paragraph({
                      children: [new TextRun({ text: `Address: ${replacePlaceholders(party.address)}`, size: 20 })],
                      spacing: { after: 150 }
                    }));
                  } else {
                    children.push(new Paragraph({
                      children: [new TextRun({ text: 'Address: ________________________________________________________________________', size: 20 })],
                      spacing: { after: 150 }
                    }));
                  }
                  
                  // Occupation field
                  let occupation = '';
                  if (party.signerTitle) {
                    occupation = replacePlaceholders(party.signerTitle);
                  } else if (party.type && party.type !== 'individual') {
                    occupation = replacePlaceholders(party.type);
                  }
                  
                  if (occupation) {
                    children.push(new Paragraph({
                      children: [new TextRun({ text: `Occupation: ${occupation}`, size: 20 })],
                      spacing: { after: 150 }
                    }));
                  } else {
                    children.push(new Paragraph({
                      children: [new TextRun({ text: 'Occupation: ________________________________________________________________________', size: 20 })],
                      spacing: { after: 150 }
                    }));
                  }
                  
                  // Signature and date lines
                  children.push(new Paragraph({
                    children: [new TextRun({ text: 'Signature: _____________________________________________________________________    Date: ________________________', size: 20 })],
                    spacing: { after: 200 }
                  }));
                  
                  // Witness section (matching PDF)
                  children.push(new Paragraph({
                    children: [new TextRun({ text: 'IN THE PRESENCE OF:', bold: true, size: 18 })],
                    spacing: { before: 150, after: 150 },
                    indent: { left: 720 }
                  }));
                  
                  children.push(new Paragraph({
                    children: [new TextRun({ text: 'Name: __________________________________________________________________________', size: 18 })],
                    spacing: { after: 100 },
                    indent: { left: 720 }
                  }));
                  
                  children.push(new Paragraph({
                    children: [new TextRun({ text: 'Address: __________________________________________________________________________', size: 18 })],
                    spacing: { after: 100 },
                    indent: { left: 720 }
                  }));
                  
                  children.push(new Paragraph({
                    children: [new TextRun({ text: 'Occupation: __________________________________________________________________________', size: 18 })],
                    spacing: { after: 100 },
                    indent: { left: 720 }
                  }));
                  
                  children.push(new Paragraph({
                    children: [new TextRun({ text: 'Signature: ___________________________________________________________________    Date: ________________________', size: 18 })],
                    spacing: { after: 100 },
                    indent: { left: 720 }
                  }));
                  
                  children.push(new Paragraph({
                    children: [new TextRun({ text: 'Tel: __________________________________________________________________________', size: 18 })],
                    spacing: { after: 400 },
                    indent: { left: 720 }
                  }));
                });
              }
            }
          });
        } else {
          // Fallback for old text-based format (same as PDF fallback)
          let contractFullContent = '';
          if (isPlainText) {
            contractFullContent = improvedObj;
          } else {
            contractFullContent = improvedObj.text || improvedObj.content || JSON.stringify(improvedObj, null, 2);
          }
          
          const paragraphs = contractFullContent.split(/\n\s*\n/).filter(p => p.trim().length > 0);
          let contractTitle = 'CONTRACT AGREEMENT';
          
          // Extract title from first lines
          const firstLines = contractFullContent.split('\n').slice(0, 5);
          for (let i = 0; i < firstLines.length; i++) {
            const line = firstLines[i].trim();
            if (line.length > 0 && 
                (line.toUpperCase().includes('AGREEMENT') || line.toUpperCase().includes('CONTRACT')) &&
                line.length < 100) {
              contractTitle = line.toUpperCase();
              break;
            }
          }
          
          // Contract header - Black, Centered
          children.push(new Paragraph({
            children: [new TextRun({ text: contractTitle, bold: true, size: 32 })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 600 }
          }));
          
          // Process content sections
          let titleProcessed = false;
          
          paragraphs.forEach((paragraph) => {
            const cleanParagraph = paragraph.trim();
            if (!cleanParagraph) return;
            
            if (!titleProcessed && cleanParagraph.toUpperCase() === contractTitle) {
              titleProcessed = true;
              return;
            }
            
            const isMainSectionHeader = cleanParagraph.match(/^\d+\.\s+[A-Z]/);
            const isSubSectionHeader = cleanParagraph.match(/^\d+\.\d+\s/);
            
            if (isMainSectionHeader) {
              children.push(new Paragraph({
                children: [new TextRun({ text: cleanParagraph, bold: true, size: 24 })],
                spacing: { before: 400, after: 200 }
              }));
            } else if (isSubSectionHeader) {
              children.push(new Paragraph({
                children: [new TextRun({ text: cleanParagraph, bold: true, size: 22 })],
                spacing: { before: 200, after: 150 }
              }));
            } else {
              children.push(new Paragraph({
                children: [new TextRun({ text: cleanParagraph, size: 22 })],
                alignment: AlignmentType.JUSTIFIED,
                spacing: { after: 200 }
              }));
            }
          });
        }

      } else if (docType.toLowerCase() === 'cv') {
        console.log('CV DOCX Processing Started - Matching PDF Template');

        let cvData = null;
        let purpose = 'work';
        
        // Handle CV data extraction (same logic as PDF)
        if (!isPlainText && improvedObj.purpose && improvedObj.full_name && improvedObj.experience) {
          cvData = improvedObj;
          purpose = improvedObj.purpose || 'work';
        }

        if (cvData) {
          // Get purpose from context if available
          if (revampData?.context_json?.purpose) {
            purpose = revampData.context_json.purpose.toLowerCase();
          }

          // CV HEADER - Centered Name (matching PDF)
          const fullName = cvData.full_name || 'Your Name';
          children.push(new Paragraph({
            children: [new TextRun({ text: fullName.toUpperCase(), bold: true, size: 48 })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 300 }
          }));

          // Contact Information - Centered (matching PDF)
          const firstLineContact = [];
          if (cvData.email) firstLineContact.push(cvData.email);
          if (cvData.phone) firstLineContact.push(cvData.phone);
          
          if (firstLineContact.length > 0) {
            const firstContactLine = firstLineContact.join(' | ');
            children.push(new Paragraph({
              children: [new TextRun({ text: firstContactLine, size: 24 })],
              alignment: AlignmentType.CENTER,
              spacing: { after: 150 }
            }));
          }
          
          if (cvData.address) {
            children.push(new Paragraph({
              children: [new TextRun({ text: cvData.address, size: 24 })],
              alignment: AlignmentType.CENTER,
              spacing: { after: 150 }
            }));
          }
          
          if (cvData.linkedin) {
            children.push(new Paragraph({
              children: [new TextRun({ text: cvData.linkedin, size: 24 })],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }));
          }

          // Career Objective - Blue header (matching PDF)
          if (cvData.summary) {
            children.push(new Paragraph({
              children: [new TextRun({ 
                text: 'CAREER STRATEGIC PURPOSE', 
                bold: true, 
                size: 28,
                color: "2563EB" // Blue color like PDF
              })],
              spacing: { before: 300, after: 200 }
            }));
            
            children.push(new Paragraph({
              children: [new TextRun({ text: cvData.summary, size: 22 })],
              alignment: AlignmentType.JUSTIFIED,
              spacing: { after: 400 }
            }));
          }

          // Personal Biodata - Blue header (matching PDF)
          if (cvData.personal_info && Object.keys(cvData.personal_info).length > 0) {
            children.push(new Paragraph({
              children: [new TextRun({ 
                text: 'PERSONAL BIODATA', 
                bold: true, 
                size: 28,
                color: "2563EB" // Blue color
              })],
              spacing: { before: 300, after: 200 }
            }));

            const personalInfoItems = [];
            if (cvData.personal_info.date_of_birth) personalInfoItems.push(['Date of Birth:', cvData.personal_info.date_of_birth]);
            if (cvData.personal_info.gender) personalInfoItems.push(['Gender:', cvData.personal_info.gender]);
            if (cvData.personal_info.nationality) personalInfoItems.push(['Nationality:', cvData.personal_info.nationality]);
            if (cvData.personal_info.interests) personalInfoItems.push(['Interests:', cvData.personal_info.interests]);

            // Add preferred call name if available
            if (cvData.full_name) {
              const firstName = cvData.full_name.split(' ')[0];
              personalInfoItems.push(['Preferred Call Name:', firstName]);
            }

            personalInfoItems.forEach(([label, value]) => {
              children.push(new Paragraph({
                children: [
                  new TextRun({ text: `${label} `, bold: true, size: 22 }),
                  new TextRun({ text: value, size: 22 })
                ],
                spacing: { after: 150 }
              }));
            });

            children.push(new Paragraph({
              children: [new TextRun({ text: '', size: 12 })],
              spacing: { after: 300 }
            }));
          }

          // Work Experience - Blue header (matching PDF)
          if (cvData.experience && cvData.experience.length > 0) {
            children.push(new Paragraph({
              children: [new TextRun({ 
                text: 'WORK EXPERIENCE', 
                bold: true, 
                size: 28,
                color: "2563EB" // Blue color
              })],
              spacing: { before: 300, after: 200 }
            }));

            cvData.experience.forEach((job, jobIndex) => {
              // Company and role
              const jobTitle = job.job_title || job.title || 'Job Title';
              const company = job.company || 'Company';
              const location = job.location || '';

              children.push(new Paragraph({
                children: [new TextRun({ text: `${company}: ${jobTitle}`, bold: true, size: 24 })],
                spacing: { after: 150 }
              }));

              // Location with red arrow (matching PDF)
              children.push(new Paragraph({
                children: [
                  new TextRun({ text: '> ', color: "DC143C" }), // Red arrow
                  new TextRun({ text: `Location: ${location || 'Not specified'}`, size: 22 })
                ],
                spacing: { after: 150 }
              }));

              // Duration with red arrow (matching PDF)
              const duration = job.duration || 'Duration not specified';
              children.push(new Paragraph({
                children: [
                  new TextRun({ text: '< ', color: "DC143C" }), // Red arrow
                  new TextRun({ text: `Duration: ${duration}`, size: 22 })
                ],
                spacing: { after: 200 }
              }));

              // Job Description header - Red (matching PDF)
              children.push(new Paragraph({
                children: [new TextRun({ 
                  text: 'Job Description and Key Achievements', 
                  bold: true, 
                  size: 22,
                  color: "DC143C" // Red color
                })],
                spacing: { after: 150 }
              }));

              // Achievements - Different format based on purpose (matching PDF)
              if (job.achievements) {
                if (purpose === 'work') {
                  // WORK PURPOSE: Use bullet points
                  let achievementItems = [];
                  if (Array.isArray(job.achievements)) {
                    achievementItems = job.achievements;
                  } else {
                    achievementItems = job.achievements.split(/[.!?]\s+/).filter(s => s.trim());
                  }

                  achievementItems.forEach((achievement) => {
                    let cleanAchievement = achievement.trim();
                    if (!cleanAchievement.endsWith('.') && !cleanAchievement.endsWith('!') && !cleanAchievement.endsWith('?')) {
                      cleanAchievement += '.';
                    }

                    children.push(new Paragraph({
                      children: [new TextRun({ text: `• ${cleanAchievement}`, size: 22 })],
                      spacing: { after: 150 }
                    }));
                  });
                } else {
                  // STUDY PURPOSE: Use paragraph format
                  let achievementText = '';
                  if (Array.isArray(job.achievements)) {
                    achievementText = job.achievements.join('. ') + '.';
                  } else {
                    achievementText = job.achievements;
                  }

                  children.push(new Paragraph({
                    children: [new TextRun({ text: achievementText, size: 22 })],
                    alignment: AlignmentType.JUSTIFIED,
                    spacing: { after: 200 }
                  }));
                }
              }

              // Add space between jobs
              if (jobIndex < cvData.experience.length - 1) {
                children.push(new Paragraph({
                  children: [new TextRun({ text: '', size: 12 })],
                  spacing: { after: 300 }
                }));
              }
            });

            children.push(new Paragraph({
              children: [new TextRun({ text: '', size: 12 })],
              spacing: { after: 400 }
            }));
          }

          // Education - Blue header (matching PDF)
          if (cvData.education && cvData.education.length > 0) {
            children.push(new Paragraph({
              children: [new TextRun({ 
                text: 'EDUCATION AND TRAINING', 
                bold: true, 
                size: 28,
                color: "2563EB" // Blue color
              })],
              spacing: { before: 300, after: 200 }
            }));

            cvData.education.forEach((edu, eduIndex) => {
              const degree = edu.degree || 'Degree';
              const institution = edu.institution || 'Institution';
              const duration = edu.duration || '';

              children.push(new Paragraph({
                children: [new TextRun({ text: `${degree} (${institution})`, size: 22 })],
                spacing: { after: 150 }
              }));

              children.push(new Paragraph({
                children: [new TextRun({ text: `${institution} | ${duration}`, size: 22 })],
                spacing: { after: 150 }
              }));

              if (edu.details && edu.details.trim()) {
                children.push(new Paragraph({
                  children: [new TextRun({ text: edu.details, size: 22 })],
                  spacing: { after: 150 }
                }));
              }

              if (eduIndex < cvData.education.length - 1) {
                children.push(new Paragraph({
                  children: [new TextRun({ text: '', size: 12 })],
                  spacing: { after: 200 }
                }));
              }
            });

            children.push(new Paragraph({
              children: [new TextRun({ text: '', size: 12 })],
              spacing: { after: 400 }
            }));
          }

          // Skills - Blue header (matching PDF)
          if (cvData.skills && cvData.skills.length > 0) {
            children.push(new Paragraph({
              children: [new TextRun({ 
                text: 'SKILLS', 
                bold: true, 
                size: 28,
                color: "2563EB" // Blue color
              })],
              spacing: { before: 300, after: 400 }
            }));

            if (purpose === 'work') {
              // WORK PURPOSE: Use bullet points (matching PDF)
              cvData.skills.forEach((skill) => {
                children.push(new Paragraph({
                  children: [new TextRun({ text: `• ${skill}`, size: 22 })],
                  spacing: { after: 150 }
                }));
              });
            } else {
              // STUDY PURPOSE: Use paragraph format (matching PDF)
              const skillsText = cvData.skills.join(', ') + '.';
              children.push(new Paragraph({
                children: [new TextRun({ text: skillsText, size: 22 })],
                alignment: AlignmentType.JUSTIFIED,
                spacing: { after: 200 }
              }));
            }

            children.push(new Paragraph({
              children: [new TextRun({ text: '', size: 12 })],
              spacing: { after: 400 }
            }));
          }

          // Certifications - Blue header (matching PDF)
          if (cvData.certifications && cvData.certifications.length > 0) {
            children.push(new Paragraph({
              children: [new TextRun({ 
                text: 'CERTIFICATIONS', 
                bold: true, 
                size: 28,
                color: "2563EB" // Blue color
              })],
              spacing: { before: 300, after: 200 }
            }));

            if (purpose === 'work') {
              // WORK PURPOSE: Use bullet points
              cvData.certifications.forEach((cert) => {
                let certText = cert.name;
                if (cert.issuer) certText += ` (${cert.issuer})`;
                if (cert.date) certText += ` - ${cert.date}`;

                children.push(new Paragraph({
                  children: [new TextRun({ text: `• ${certText}`, size: 22 })],
                  spacing: { after: 150 }
                }));
              });
            } else {
              // STUDY PURPOSE: Use paragraph format
              const certTexts = cvData.certifications.map(cert => {
                let certText = cert.name;
                if (cert.issuer) certText += ` from ${cert.issuer}`;
                if (cert.date) certText += ` (${cert.date})`;
                return certText;
              });

              const certificationsText = certTexts.join(', ') + '.';
              children.push(new Paragraph({
                children: [new TextRun({ text: certificationsText, size: 22 })],
                alignment: AlignmentType.JUSTIFIED,
                spacing: { after: 200 }
              }));
            }

            children.push(new Paragraph({
              children: [new TextRun({ text: '', size: 12 })],
              spacing: { after: 400 }
            }));
          }

          // References - Blue header (matching PDF)
          children.push(new Paragraph({
            children: [new TextRun({ 
              text: 'REFERENCES', 
              bold: true, 
              size: 28,
              color: "2563EB" // Blue color
            })],
            spacing: { before: 300, after: 400 }
          }));
          children.push(new Paragraph({
            children: [new TextRun({ text: 'Available on request', size: 22 })],
            spacing: { after: 200 }
          }));

        } else {
          // Fallback for CV without structured data
          children.push(new Paragraph({
            children: [new TextRun({ text: `Enhanced ${getDocTypeTitle(docType)}`, bold: true, size: 32 })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 600 }
          }));

          const content = isPlainText ? improvedObj : (improvedObj.text || renderDynamicCV(improvedObj));
          const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());

          paragraphs.forEach(paragraph => {
            children.push(new Paragraph({
              children: [new TextRun({ text: paragraph.trim(), size: 22 })],
              spacing: { after: 200 }
            }));
          });
        }

      } else {
        // Generic document type
        children.push(new Paragraph({
          children: [new TextRun({ text: `Enhanced ${getDocTypeTitle(docType)}`, bold: true, size: 32 })],
          alignment: AlignmentType.CENTER,
          spacing: { before: 0, after: 600 }
        }));

        const content = isPlainText ? improvedObj : (improvedObj.text || JSON.stringify(improvedObj, null, 2));
        const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());

        paragraphs.forEach(paragraph => {
          children.push(new Paragraph({
            children: [new TextRun({ text: paragraph.trim(), size: 22 })],
            spacing: { after: 200 }
          }));
        });
      }

      // Create and save document with proper section structure
      const document = new DocxDocument({
        creator: "DocxsAi",
        title: `Enhanced ${getDocTypeTitle(docType)}`,
        description: `Enhanced ${docType} document`,
        sections: [{
          children: children
        }]
      });
      
      const blob = await Packer.toBlob(document);
      saveAs(blob, `${docType}-enhanced-${id}.docx`);
      toast.success('DOCX downloaded successfully!');
      console.log('DOCX saved successfully - matching PDF template design');

    } catch (error) {
      console.error('Error creating DOCX:', error);
      toast.error(`Failed to create DOCX: ${error.message}`);
    }
  };

  // const handleDownloadDocx = async () => {
  //   if (!revamped) return;
  //   let improvedObj;
  //   try {
  //     improvedObj = typeof revamped === 'string' ? JSON.parse(revamped) : revamped;
  //   } catch {
  //     toast.error('Could not parse enhanced document for DOCX export.');
  //     return;
  //   }
  //   const doc = new DocxDocument();
  //   const children = [];
    
  //   if (docType.toLowerCase() === 'contract') {
  //     // Check for new standardized format first
  //     if (improvedObj.text && typeof improvedObj.text === 'string') {
  //       // AI has provided standardized contract format - parse it for enhanced DOCX
  //       const contractText = improvedObj.text;
  //       const paragraphs = contractText.split(/\n\s*\n/).filter(p => p.trim());
        
  //       paragraphs.forEach((paragraph, index) => {
  //         const cleanParagraph = paragraph.trim();
  //         if (!cleanParagraph) return;
          
  //         // Enhanced section detection for better formatting
  //         const isContractTitle = index === 0 && (cleanParagraph.toUpperCase().includes('AGREEMENT') || cleanParagraph.toUpperCase().includes('CONTRACT'));
  //         const isMainSection = cleanParagraph.match(/^\d+\.\s+[A-Z]/) && !cleanParagraph.match(/^\d+\.\d+/);
  //         const isSubSection = cleanParagraph.match(/^\d+\.\d+\s/);
  //         const isIntroduction = cleanParagraph.includes('This Agreement') || cleanParagraph.includes('This Contract') || cleanParagraph.includes('between:');
  //         const isSignature = cleanParagraph.toUpperCase().includes('SIGNATURE') || cleanParagraph.toUpperCase().includes('EXECUTION') || cleanParagraph.toUpperCase().includes('IN WITNESS WHEREOF');
          
  //         if (isContractTitle) {
  //           // Contract title with enhanced styling
  //           children.push(new Paragraph({
  //             text: cleanParagraph,
  //             heading: HeadingLevel.TITLE,
  //             alignment: 'center'
  //           }));
  //           children.push(new Paragraph(''));
            
  //         } else if (isMainSection) {
  //           // Main sections (1. INTRODUCTION, 2. DEFINITIONS, etc.)
  //           children.push(new Paragraph({ 
  //             text: cleanParagraph, 
  //             heading: HeadingLevel.HEADING_1,
  //             spacing: { before: 240, after: 120 }
  //           }));
            
  //         } else if (isSubSection) {
  //           // Sub-sections (3.1, 3.2, etc.)
  //           children.push(new Paragraph({ 
  //             text: cleanParagraph, 
  //             heading: HeadingLevel.HEADING_2,
  //             spacing: { before: 120, after: 60 },
  //             indent: { left: 360 }
  //           }));
            
  //         } else if (isIntroduction) {
  //           // Introduction paragraphs with special formatting
  //           children.push(new Paragraph({
  //             text: cleanParagraph,
  //             spacing: { before: 120, after: 120 },
  //             indent: { left: 240 }
  //           }));
            
  //         } else if (isSignature) {
  //           // Signature section with enhanced formatting
  //           if (cleanParagraph.includes('parties have executed') || cleanParagraph.includes('IN WITNESS WHEREOF')) {
  //             children.push(new Paragraph({
  //               text: cleanParagraph,
  //               spacing: { before: 240, after: 240 },
  //               alignment: 'center'
  //             }));
              
  //             // Add signature lines
  //             children.push(new Paragraph(''));
  //             children.push(new Paragraph({
  //               text: '________________________________                    ________________________________',
  //               spacing: { before: 480, after: 120 }
  //             }));
  //             children.push(new Paragraph({
  //               text: 'Party A Signature                                                    Party B Signature'
  //             }));
  //             children.push(new Paragraph(''));
  //             children.push(new Paragraph({
  //               text: 'Name: ___________________________________                    Name: ___________________________________'
  //             }));
  //             children.push(new Paragraph({
  //               text: 'Title: ____________________________________                     Title: ____________________________________'
  //             }));
  //             children.push(new Paragraph({
  //               text: 'Date: ____________________________________                     Date: ____________________________________'
  //             }));
              
  //           } else {
  //             children.push(new Paragraph({
  //               text: cleanParagraph,
  //               heading: HeadingLevel.HEADING_2,
  //               alignment: 'center',
  //               spacing: { before: 240, after: 120 }
  //             }));
  //           }
            
  //         } else {
  //           // Regular content with proper indentation and spacing
  //           children.push(new Paragraph({
  //             text: cleanParagraph,
  //             spacing: { after: 120 },
  //             indent: { left: 360 }
  //           }));
  //         }
  //       });
  //     } else if (improvedObj.title && improvedObj.sections && improvedObj.parties) {
  //       // Handle NEW STRUCTURED FORMAT for DOCX
  //       console.log('Using NEW structured contract format for DOCX');
        
  //       // Contract Title
  //       children.push(new Paragraph({ 
  //         text: replacePlaceholders(improvedObj.title), 
  //         heading: HeadingLevel.TITLE,
  //         alignment: 'center' 
  //       }));
  //       children.push(new Paragraph(''));
        
  //       // Parties Section
  //       if (improvedObj.parties && Array.isArray(improvedObj.parties)) {
  //         children.push(new Paragraph({ 
  //           text: 'PARTIES TO THIS AGREEMENT', 
  //           heading: HeadingLevel.HEADING_1 
  //         }));
          
  //         const agreementDate = improvedObj.agreementDate || new Date().toLocaleDateString();
          
  //         children.push(new Paragraph({
  //           text: `This Agreement is entered into on ${agreementDate}, between:`
  //         }));
  //         children.push(new Paragraph(''));
          
  //                    improvedObj.parties.forEach((party, index) => {
  //            let partyText = `${index + 1}. ${replacePlaceholders(party.name)}`;
  //            if (party.type && party.type !== 'individual') {
  //              partyText += ` (${replacePlaceholders(party.type)})`;
  //            }
  //            if (party.address) {
  //              partyText += ` - ${replacePlaceholders(party.address)}`;
  //            }
  //            partyText += ` - referred to as the "${replacePlaceholders(party.role)}"`;
             
  //            children.push(new Paragraph({ text: partyText }));
  //          });
  //         children.push(new Paragraph(''));
  //       }
        
  //       // Sections
  //       if (improvedObj.sections && Array.isArray(improvedObj.sections)) {
  //         improvedObj.sections.forEach((section) => {
  //           // Main section header
  //           if (section.title) {
  //             children.push(new Paragraph({ 
  //               text: `${section.number}. ${section.title}`, 
  //               heading: HeadingLevel.HEADING_1,
  //               spacing: { before: 240, after: 120 }
  //             }));
  //           }
            
  //                                    // Section content
  //           if (section.content && section.content.trim()) {
  //             children.push(new Paragraph({ 
  //               text: formatContentWithLists(section.content),
  //               spacing: { after: 120 }
  //             }));
  //           }
            
  //                        // Subsections
  //            if (section.subsections && Array.isArray(section.subsections)) {
  //              section.subsections.forEach((subsection) => {
  //                children.push(new Paragraph({ 
  //                  text: `${subsection.number} ${subsection.title}`, 
  //                  heading: HeadingLevel.HEADING_2,
  //                  spacing: { before: 120, after: 60 }
  //                }));
                 
  //                // Handle numbered items if they exist
  //                if (subsection.numbered_items && Array.isArray(subsection.numbered_items)) {
  //                  subsection.numbered_items.forEach((item) => {
  //                    children.push(new Paragraph({ 
  //                      text: `${item.number}. ${replacePlaceholders(item.content)}`,
  //                      spacing: { after: 120 }
  //                    }));
  //                  });
  //                } else if (subsection.content) {
  //                  // Skip signature content as it will be handled specially
  //                  if (subsection.title && subsection.title.toLowerCase().includes('signature')) {
  //                    // Skip - will be handled by special signature section
  //                  } else {
  //                    children.push(new Paragraph({ 
  //                      text: formatContentWithLists(subsection.content),
  //                      spacing: { after: 120 }
  //                    }));
  //                  }
  //                }
  //              });
  //            }
            
  //           // Special handling for EXECUTION section
  //           if (section.number === "6" && section.title === "EXECUTION") {
  //             // Add witness statement first
  //             children.push(new Paragraph({ 
  //               text: 'IN WITNESS WHEREOF the Parties have hereunto set their respective hands and seals the day and year first mentioned.',
  //               spacing: { before: 240, after: 180 },
  //               alignment: AlignmentType.JUSTIFIED
  //             }));
              
  //             if (improvedObj.parties && Array.isArray(improvedObj.parties)) {
  //               improvedObj.parties.forEach((party, partyIndex) => {
  //                 // Party signature header
  //                 children.push(new Paragraph({
  //                   text: `SIGNED, SEALED AND DELIVERED by the within named ${party.role.toUpperCase()}:`,
  //                   bold: true,
  //                   spacing: { before: 120, after: 60 }
  //                 }));
                  
  //                 // Name field
  //                 let partyName = replacePlaceholders(party.name);
  //                 if (party.signerName && party.signerName !== party.name) {
  //                   partyName = replacePlaceholders(party.signerName);
  //                 }
  //                 children.push(new Paragraph({
  //                   text: `Name: ${partyName}`,
  //                   spacing: { after: 60 }
  //                 }));
                  
  //                 // Address field
  //                 if (party.address) {
  //                   children.push(new Paragraph({
  //                     text: `Address: ${replacePlaceholders(party.address)}`,
  //                     spacing: { after: 60 }
  //                   }));
  //                 } else {
  //                   children.push(new Paragraph({
  //                     text: 'Address: ________________________________________________________________________',
  //                     spacing: { after: 60 }
  //                   }));
  //                 }
                  
  //                 // Occupation field
  //                 let occupation = '';
  //                 if (party.signerTitle) {
  //                   occupation = replacePlaceholders(party.signerTitle);
  //                 } else if (party.type && party.type !== 'individual') {
  //                   occupation = replacePlaceholders(party.type);
  //                 }
                  
  //                 if (occupation) {
  //                   children.push(new Paragraph({
  //                     text: `Occupation: ${occupation}`,
  //                     spacing: { after: 60 }
  //                   }));
  //                 } else {
  //                   children.push(new Paragraph({
  //                     text: 'Occupation: ________________________________________________________________________',
  //                     spacing: { after: 60 }
  //                   }));
  //                 }
                  
  //                 // Signature and date lines
  //                 children.push(new Paragraph({
  //                   text: 'Signature: _____________________________________________________________________    Date: ________________________',
  //                   spacing: { after: 120 }
  //                 }));
                  
  //                 // Witness section
  //                 children.push(new Paragraph({
  //                   text: 'IN THE PRESENCE OF:',
  //                   bold: true,
  //                   spacing: { before: 60, after: 60 },
  //                   indent: { left: 720 }
  //                 }));
                  
  //                 children.push(new Paragraph({
  //                   text: 'Name: __________________________________________________________________________',
  //                   spacing: { after: 40 },
  //                   indent: { left: 720 }
  //                 }));
                  
  //                 children.push(new Paragraph({
  //                   text: 'Address: __________________________________________________________________________',
  //                   spacing: { after: 40 },
  //                   indent: { left: 720 }
  //                 }));
                  
  //                 children.push(new Paragraph({
  //                   text: 'Occupation: __________________________________________________________________________',
  //                   spacing: { after: 40 },
  //                   indent: { left: 720 }
  //                 }));
                  
  //                 children.push(new Paragraph({
  //                   text: 'Signature: ___________________________________________________________________    Date: ________________________',
  //                   spacing: { after: 40 },
  //                   indent: { left: 720 }
  //                 }));
                  
  //                 children.push(new Paragraph({
  //                   text: 'Tel: __________________________________________________________________________',
  //                   spacing: { after: 120 },
  //                   indent: { left: 720 }
  //                 }));
                  
  //                 // Add extra space between parties
  //                 if (partyIndex < improvedObj.parties.length - 1) {
  //                   children.push(new Paragraph({
  //                     text: '',
  //                     spacing: { after: 180 }
  //                   }));
  //                 }
  //               });
  //             }
  //           }
  //         });
  //       }
        
  //     } else {
  //       // Fallback: Handle old unstructured format
  //       if (improvedObj.contract_title) {
  //         children.push(new Paragraph({ text: improvedObj.contract_title, heading: HeadingLevel.HEADING_1 }));
  //         children.push(new Paragraph(''));
  //       }
  //       if (improvedObj.parties) {
  //         const company = improvedObj.parties.company;
  //         const freelancer = improvedObj.parties.freelancer;
  //         if (company && freelancer) {
  //           children.push(new Paragraph({
  //             children: [
  //               new TextRun({
  //                 text: `This Freelance Agreement ("Agreement") is entered into on ${improvedObj.agreement_date || '[Date]'}, between ${company.name} (${company.type}), referred to as the "Company", and ${freelancer.name}, an ${freelancer.type}, referred to as the "Designer".`,
  //                 break: 1
  //               })
  //             ]
  //           }));
  //           children.push(new Paragraph(''));
  //         }
  //       }
  //       if (improvedObj.sections && Array.isArray(improvedObj.sections)) {
  //         improvedObj.sections.forEach((section, idx) => {
  //           if (section.title) {
  //             children.push(new Paragraph({ text: `${idx + 1}. ${section.title.toUpperCase()}`, heading: HeadingLevel.HEADING_2 }));
  //           }
  //           if (section.content) {
  //             children.push(new Paragraph({ text: section.content }));
  //             children.push(new Paragraph(''));
  //           }
  //         });
  //       }
  //       if (improvedObj.terms && Array.isArray(improvedObj.terms)) {
  //         children.push(new Paragraph({ text: 'TERMS AND CONDITIONS', heading: HeadingLevel.HEADING_2 }));
  //         improvedObj.terms.forEach((term, idx) => {
  //           children.push(new Paragraph({ text: `${idx + 1}. ${term}` }));
  //         });
  //         children.push(new Paragraph(''));
  //       }
  //       if (improvedObj.signature_block) {
  //         children.push(new Paragraph({ text: improvedObj.signature_block }));
  //       }
  //     }
  //   } 
  //   else if (docType.toLowerCase() === 'cv') {
  //     console.log('CV DOCX Processing Started - Creating Professional Layout');
      
  //     let cvData;
  //     let purpose = 'work'; // default
      
  //     // Handle both old text format and new structured format
  //     if (improvedObj.purpose && improvedObj.full_name && improvedObj.experience) {
  //       console.log('Using NEW structured CV format for DOCX');
  //       cvData = improvedObj;
  //       purpose = improvedObj.purpose || 'work';
  //     } else if (improvedObj.text) {
  //       console.log('CV has text field - using old format fallback for DOCX');
  //       // Old format fallback
  //       children.push(new Paragraph({ text: `Enhanced ${getDocTypeTitle(docType)}`, heading: HeadingLevel.HEADING_1 }));
  //       children.push(new Paragraph(''));
        
  //       const content = improvedObj.text;
  //       const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());
        
  //       paragraphs.forEach(paragraph => {
  //         children.push(new Paragraph({ text: paragraph.trim() }));
  //         children.push(new Paragraph(''));
  //       });
  //     } else {
  //       console.log('CV format not recognized - using generic fallback for DOCX');
  //       children.push(new Paragraph({ text: `Enhanced ${getDocTypeTitle(docType)}`, heading: HeadingLevel.HEADING_1 }));
  //       children.push(new Paragraph(''));
        
  //       const content = renderJsonAsText(improvedObj);
  //       const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());
        
  //       paragraphs.forEach(paragraph => {
  //         children.push(new Paragraph({ text: paragraph.trim() }));
  //         children.push(new Paragraph(''));
  //       });
  //     }
      
  //     // Only proceed if we have structured data
  //     if (cvData) {
  //       // Get purpose from context if available
  //       if (revampData?.context_json?.purpose) {
  //         purpose = revampData.context_json.purpose.toLowerCase();
  //       }
        
  //       console.log('CV Purpose for DOCX:', purpose);
        
  //       // ====== CV HEADER ======
  //       // Name - Large, Bold, Centered
  //       const fullName = cvData.full_name || 'Your Name';
  //       children.push(new Paragraph({
  //         text: fullName.toUpperCase(),
  //         heading: HeadingLevel.TITLE,
  //         alignment: AlignmentType.CENTER,
  //         spacing: { before: 240, after: 120 }
  //       }));
        
  //       // Professional title (if available from guide statement)
  //       const guideStatement = revampData?.context_json?.guide_statement || '';
  //       let professionalTitle = '';
        
  //       // Try to extract job role from guide statement if mentioned
  //       const jobRoleMatch = guideStatement.match(/target job role|role|position|software engineer|developer|analyst/i);
  //       if (jobRoleMatch || guideStatement.includes('Software Engineering')) {
  //         professionalTitle = 'Software Engineer'; // Default based on the guide statement content
  //       }
        
  //       if (professionalTitle) {
  //         children.push(new Paragraph({
  //           text: professionalTitle,
  //           alignment: AlignmentType.CENTER,
  //           spacing: { after: 120 }
  //         }));
  //       }
        
  //       // Contact Information - Centered
  //       const contactInfo = [];
  //       if (cvData.phone) contactInfo.push(cvData.phone);
  //       if (cvData.email) contactInfo.push(cvData.email);
  //       if (cvData.linkedin) contactInfo.push(cvData.linkedin);
  //       if (cvData.address) contactInfo.push(cvData.address);
        
  //       // Display contact info in rows
  //       for (let i = 0; i < contactInfo.length; i += 2) {
  //         let contactRow = contactInfo[i];
  //         if (i + 1 < contactInfo.length) {
  //           contactRow += ` | ${contactInfo[i + 1]}`;
  //         }
          
  //         children.push(new Paragraph({
  //           text: contactRow,
  //           alignment: AlignmentType.CENTER,
  //           spacing: { after: 60 }
  //         }));
  //       }
        
  //       children.push(new Paragraph({
  //         text: '',
  //         spacing: { after: 240 }
  //       }));
        
  //       // ====== TWO COLUMN LAYOUT TABLE ======
  //       const tableRows = [{
  //         children: [
  //           {
  //             children: [
  //               // LEFT COLUMN CONTENT
                
  //               // Personal Information (if available)
  //               ...(cvData.personal_info && Object.keys(cvData.personal_info).length > 0 ? [
  //                 new Paragraph({
  //                   text: 'PERSONAL INFORMATION',
  //                   heading: HeadingLevel.HEADING_2,
  //                   spacing: { before: 120, after: 120 }
  //                 }),
                  
  //                 ...(cvData.personal_info.date_of_birth ? [new Paragraph({
  //                   text: `Date of Birth: ${cvData.personal_info.date_of_birth}`,
  //                   spacing: { after: 60 }
  //                 })] : []),
                  
  //                 ...(cvData.personal_info.gender ? [new Paragraph({
  //                   text: `Gender: ${cvData.personal_info.gender}`,
  //                   spacing: { after: 60 }
  //                 })] : []),
                  
  //                 ...(cvData.personal_info.nationality ? [new Paragraph({
  //                   text: `Nationality: ${cvData.personal_info.nationality}`,
  //                   spacing: { after: 60 }
  //                 })] : []),
                  
  //                 ...(cvData.personal_info.interests ? [new Paragraph({
  //                   text: `Interests: ${cvData.personal_info.interests}`,
  //                   spacing: { after: 120 }
  //                 })] : [])
  //               ] : []),
                
  //               // Skills Section
  //               ...(cvData.skills && cvData.skills.length > 0 ? [
  //                 new Paragraph({
  //                   text: 'SKILLS',
  //                   heading: HeadingLevel.HEADING_2,
  //                   spacing: { before: 120, after: 120 }
  //                 }),
  //                 ...cvData.skills.map(skill => new Paragraph({
  //                   text: `• ${skill}`,
  //                   spacing: { after: 60 }
  //                 }))
  //               ] : []),
                
  //               // Certifications Section
  //               ...(cvData.certifications && cvData.certifications.length > 0 ? [
  //                 new Paragraph({
  //                   text: 'CERTIFICATIONS',
  //                   heading: HeadingLevel.HEADING_2,
  //                   spacing: { before: 120, after: 120 }
  //                 }),
  //                 ...cvData.certifications.flatMap(cert => [
  //                   new Paragraph({
  //                     text: cert.name,
  //                     bold: true,
  //                     spacing: { after: 40 }
  //                   }),
  //                   new Paragraph({
  //                     text: `${cert.issuer || ''} ${cert.date ? `(${cert.date})` : ''}`,
  //                     spacing: { after: 80 }
  //                   })
  //                 ])
  //               ] : []),
                
  //               // Languages
  //               ...(cvData.personal_info?.languages || cvData.languages ? [
  //                 new Paragraph({
  //                   text: 'LANGUAGES',
  //                   heading: HeadingLevel.HEADING_2,
  //                   spacing: { before: 120, after: 120 }
  //                 }),
  //                 new Paragraph({
  //                   text: `• ${Array.isArray(cvData.personal_info?.languages || cvData.languages) 
  //                     ? (cvData.personal_info?.languages || cvData.languages).join(', ') 
  //                     : (cvData.personal_info?.languages || cvData.languages || 'English')}`,
  //                   spacing: { after: 120 }
  //                 })
  //               ] : [])
  //             ],
  //             width: {
  //               size: 35,
  //               type: WidthType.PERCENTAGE,
  //             },
  //           },
  //           {
  //             children: [
  //               // RIGHT COLUMN CONTENT
                
  //               // Career Objective/Professional Summary
  //               ...(cvData.summary ? [
  //                 new Paragraph({
  //                   text: 'CAREER OBJECTIVE',
  //                   heading: HeadingLevel.HEADING_2,
  //                   spacing: { before: 120, after: 120 }
  //                 }),
  //                 new Paragraph({
  //                   text: cvData.summary,
  //                   alignment: AlignmentType.JUSTIFIED,
  //                   spacing: { after: 240 }
  //                 })
  //               ] : []),
                
  //               // Work Experience
  //               ...(cvData.experience && cvData.experience.length > 0 ? [
  //                 new Paragraph({
  //                   text: 'EXPERIENCE',
  //                   heading: HeadingLevel.HEADING_2,
  //                   spacing: { before: 120, after: 120 }
  //                 }),
                  
  //                 ...cvData.experience.flatMap((job, jobIndex) => {
  //                   const jobElements = [
  //                     // Job Title
  //                     new Paragraph({
  //                       text: job.job_title || job.title || 'Job Title',
  //                       bold: true,
  //                       spacing: { after: 60 }
  //                     }),
                      
  //                     // Company and Duration
  //                     new Paragraph({
  //                       text: `${job.company || 'Company'}`,
  //                       spacing: { after: 40 }
  //                     }),
  //                     new Paragraph({
  //                       text: `${job.duration || 'Duration'}${job.location ? ` | ${job.location}` : ''}`,
  //                       spacing: { after: 120 }
  //                     })
  //                   ];
                    
  //                   // Add achievements based on purpose
  //                   if (job.achievements) {
  //                     if (purpose === 'work') {
  //                       // WORK PURPOSE: Use bullet points
  //                       if (Array.isArray(job.achievements)) {
  //                         jobElements.push(...job.achievements.map(achievement => 
  //                           new Paragraph({
  //                             text: `• ${achievement}`,
  //                             spacing: { after: 60 },
  //                             indent: { left: 360 }
  //                           })
  //                         ));
  //                       } else {
  //                         // Split sentences into bullets
  //                         const sentences = job.achievements.split(/[.!?]\s+/).filter(s => s.trim());
  //                         jobElements.push(...sentences.map(sentence => 
  //                           new Paragraph({
  //                             text: `• ${sentence.trim()}${sentence.endsWith('.') ? '' : '.'}`,
  //                             spacing: { after: 60 },
  //                             indent: { left: 360 }
  //                           })
  //                         ));
  //                       }
  //                     } else {
  //                       // STUDY PURPOSE: Use paragraph format
  //                       let achievementText = '';
  //                       if (Array.isArray(job.achievements)) {
  //                         achievementText = job.achievements.join('. ') + '.';
  //                       } else {
  //                         achievementText = job.achievements;
  //                       }
                        
  //                       jobElements.push(new Paragraph({
  //                         text: achievementText,
  //                         alignment: AlignmentType.JUSTIFIED,
  //                         spacing: { after: 120 }
  //                       }));
  //                     }
  //                   }
                    
  //                   // Add spacing between jobs
  //                   if (jobIndex < cvData.experience.length - 1) {
  //                     jobElements.push(new Paragraph({
  //                       text: '',
  //                       spacing: { after: 180 }
  //                     }));
  //                   }
                    
  //                   return jobElements;
  //                 })
  //               ] : []),
                
  //               // Education
  //               ...(cvData.education && cvData.education.length > 0 ? [
  //                 new Paragraph({
  //                   text: 'ACADEMIC QUALIFICATIONS',
  //                   heading: HeadingLevel.HEADING_2,
  //                   spacing: { before: 240, after: 120 }
  //                 }),
                  
  //                 ...cvData.education.flatMap((edu, eduIndex) => [
  //                   // Institution
  //                   new Paragraph({
  //                     text: (edu.institution || 'Institution').toUpperCase(),
  //                     bold: true,
  //                     spacing: { after: 60 }
  //                   }),
                    
  //                   // Degree and Duration
  //                   new Paragraph({
  //                     text: `${edu.degree || 'Degree'} ${edu.duration || ''}`,
  //                     spacing: { after: 60 }
  //                   }),
                    
  //                   // Location
  //                   ...(edu.location ? [new Paragraph({
  //                     text: edu.location,
  //                     spacing: { after: 60 }
  //                   })] : []),
                    
  //                   // Details (always in sentence form for education)
  //                   ...(edu.details ? [new Paragraph({
  //                     text: edu.details,
  //                     alignment: AlignmentType.JUSTIFIED,
  //                     spacing: { after: 120 }
  //                   })] : []),
                    
  //                   // Spacing between education entries
  //                   ...(eduIndex < cvData.education.length - 1 ? [new Paragraph({
  //                     text: '',
  //                     spacing: { after: 120 }
  //                   })] : [])
  //                 ])
  //               ] : []),
                
  //               // References
  //               new Paragraph({
  //                 text: 'REFERENCES',
  //                 heading: HeadingLevel.HEADING_2,
  //                 spacing: { before: 240, after: 120 }
  //               }),
  //               new Paragraph({
  //                 text: 'Available upon request',
  //                 spacing: { after: 120 }
  //               })
  //             ],
  //             width: {
  //               size: 65,
  //               type: WidthType.PERCENTAGE,
  //             },
  //           },
  //         ],
  //       }];
        
  //       children.push(new Table({
  //         width: {
  //           size: 100,
  //           type: WidthType.PERCENTAGE,
  //         },
  //         rows: tableRows,
  //       }));
        
  //       console.log('CV DOCX Processing Complete - Professional Layout Created');
  //     }
  //   }
  //   else {
  //     // Generic DOCX for other document types
  //     children.push(new Paragraph({ text: `Enhanced ${getDocTypeTitle(docType)}`, heading: HeadingLevel.HEADING_1 }));
  //     children.push(new Paragraph(''));
      
  //     const content = extractSOPContent(improvedObj, revamped);
  //     const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());
      
  //     paragraphs.forEach(paragraph => {
  //       children.push(new Paragraph({ text: paragraph.trim() }));
  //       children.push(new Paragraph(''));
  //     });
  //   }
    
  //   doc.addSection({ children });
  //   const blob = await Packer.toBlob(doc);
  //   saveAs(blob, `${docType}-enhanced-${id}.docx`);
  //   toast.success('DOCX downloaded!');
  // };

  // Recursive pretty printer for any JSON structure
  function renderJsonAsText(obj, indent = 0) {
    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      return obj + '\n';
    }
    if (Array.isArray(obj)) {
      return obj.map((item, idx) => `${' '.repeat(indent)}${idx + 1}. ${renderJsonAsText(item, indent + 2)}`).join('');
    }
    if (typeof obj === 'object' && obj !== null) {
      let text = '';
      for (const [key, value] of Object.entries(obj)) {
        if (typeof value === 'object' && value !== null) {
          text += `${' '.repeat(indent)}${capitalize(key)}:\n${renderJsonAsText(value, indent + 2)}`;
        } else {
          text += `${' '.repeat(indent)}${capitalize(key)}: ${value}\n`;
        }
      }
      return text;
    }
    return '';
  }
  
  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Dynamic recursive CV renderer
  function renderDynamicCV(obj, indent = 0) {
    if (obj == null) return '';
    let text = '';
    const pad = (n) => ' '.repeat(n);

    if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
      return pad(indent) + obj + '\n';
    }
    if (Array.isArray(obj)) {
      obj.forEach((item, idx) => {
        // If array of primitives
        if (typeof item !== 'object' || item == null) {
          text += pad(indent) + '- ' + item + '\n';
        } else {
          // Array of objects (e.g., experience)
          text += renderDynamicCV(item, indent + 2);
        }
      });
      return text;
    }
    if (typeof obj === 'object') {
      for (const [key, value] of Object.entries(obj)) {
        // Prettify key
        const prettyKey = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        if (typeof value === 'object' && value !== null) {
          text += pad(indent) + prettyKey + ':\n' + renderDynamicCV(value, indent + 2);
        } else {
          text += pad(indent) + prettyKey + ': ' + value + '\n';
        }
      }
      return text;
    }
    return '';
  }

  // Helper to get title case for docType
  function getDocTypeTitle(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  const enhancedFileUrl = useMemo(() => {
    if (enhancedFormat === 'docx' && enhancedDocxUrl) return enhancedDocxUrl;
    return enhancedPdfUrl || enhancedDocxUrl;
  }, [enhancedDocxUrl, enhancedFormat, enhancedPdfUrl]);

  const renderEnhancedText = () => {
    if (docType.toLowerCase() === 'contract') {
      try {
        const improvedObj = typeof revamped === 'string' ? JSON.parse(revamped) : revamped;
        const formatted = formatEnhancedContract(improvedObj);
        if (!formatted.trim()) return renderJsonAsText(improvedObj);
        return formatted;
      } catch {
        return revamped || 'Revamped text not available';
      }
    }
    if (docType.toLowerCase() === 'sop') {
      try {
        const improvedObj = typeof revamped === 'string' ? JSON.parse(revamped) : revamped;
        const formatted = formatEnhancedSOP(improvedObj);
        if (!formatted.trim()) return renderJsonAsText(improvedObj);
        return formatted;
      } catch {
        return revamped || 'Revamped text not available';
      }
    }
    if (docType.toLowerCase() === 'cv') {
      try {
        const improvedObj = typeof revamped === 'string' ? JSON.parse(revamped) : revamped;
        return renderDynamicCV(improvedObj);
      } catch {
        return revamped || 'Revamped text not available';
      }
    }
    try {
      const improvedObj = typeof revamped === 'string' ? JSON.parse(revamped) : revamped;
      return renderJsonAsText(improvedObj);
    } catch {
      return revamped || 'Revamped text not available';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--color-text-secondary)]">Loading revamp result...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-[var(--color-danger)] text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2">Error Loading Result</h2>
          <p className="text-[var(--color-text-muted)] mb-6">{error}</p>
          <button
            onClick={() => navigate('/document-analysis')}
            className="brand-gradient-btn px-6 py-2 rounded-lg font-medium"
          >
            Back to Analysis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[var(--color-bg)] ${window.innerWidth < 768 ? 'px-1' : 'p-4'}`}>
      <div className={`${window.innerWidth < 768 ? 'max-w-4xl mx-auto' : 'max-w-7xl mx-auto'}`}>
        {/* Header */}
        <div className={`mb-6 ${window.innerWidth < 768 ? 'flex flex-col items-start gap-3' : 'flex items-center justify-between'}`}>
          <div>
            <h1 className={`text-3xl font-bold text-[var(--color-text)] mb-2 ${window.innerWidth < 768 ? 'mt-4' : ''}`}>
              {getDocTypeTitle(docType)} Revamp Result
            </h1>
            <p className="text-[var(--color-text-muted)]">Original vs. AI-Enhanced Version</p>
          </div>
          <div className="relative w-full max-w-xs">
            <button
              onClick={() => setActionsOpen((open) => !open)}
              className={`brand-gradient-btn px-4 py-2 rounded-lg font-medium flex items-center justify-between w-full`}
            >
              Actions <ChevronDown className="ml-2 w-5 h-5" />
            </button>
            {actionsOpen && (
              <div className="absolute left-0 right-0 mt-2 elevated-card rounded-lg shadow-lg z-10 w-full">
                <button
                  onClick={() => { setActionsOpen(false); navigate('/revamp-document'); }}
                  className="block w-full text-left px-4 py-3 text-[var(--color-text)] hover:bg-[var(--color-primary-soft)] rounded-t-lg"
                >
                  Revamp New Document
                </button>
                <button
                  onClick={() => { setActionsOpen(false); handleDownloadDocx(); }}
                  className="block w-full text-left px-4 py-3 text-[var(--color-text)] hover:bg-[var(--color-primary-soft)]"
                >
                  Download in Docs
                </button>
                <button
                  onClick={() => { setActionsOpen(false); handleDownloadPDF(); }}
                  className="block w-full text-left px-4 py-3 text-[var(--color-text)] hover:bg-[var(--color-primary-soft)] rounded-b-lg"
                >
                  Download in PDF
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Revamp Context Info */}
        {revampData && revampData.context_json && (
          <div className={`elevated-card rounded-lg ${window.innerWidth < 768 ? 'p-2 mb-4' : 'p-4 mb-6'}`}>
            <h3 className={`text-lg font-semibold text-[var(--color-text)] mb-3 ${window.innerWidth < 768 ? 'text-base' : ''}`}>Revamp Context</h3>
            {docType.toLowerCase() === 'sop' ? (
              <div className="space-y-4">
                {/* First row: Style, Program Type, Country */}
                <div className={`grid ${window.innerWidth < 768 ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-3 gap-4'}`}>
                  <div className="bg-[var(--color-surface-muted)] rounded p-3">
                    <div className="text-[var(--color-text-muted)] text-sm capitalize">Style</div>
                    <div className="text-[var(--color-text)] font-medium">{revampData.context_json.style || 'Not specified'}</div>
                  </div>
                  <div className="bg-[var(--color-surface-muted)] rounded p-3">
                    <div className="text-[var(--color-text-muted)] text-sm capitalize">Program Type</div>
                    <div className="text-[var(--color-text)] font-medium">{contextField(revampData.context_json, 'program_type')}</div>
                  </div>
                  <div className="bg-[var(--color-surface-muted)] rounded p-3">
                    <div className="text-[var(--color-text-muted)] text-sm capitalize">Country</div>
                    <div className="text-[var(--color-text)] font-medium">{contextField(revampData.context_json, 'country')}</div>
                  </div>
                </div>
                {/* Second row: Institution, Course Of Study */}
                <div className={`grid ${window.innerWidth < 768 ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-4'}`}>
                  <div className="bg-[var(--color-surface-muted)] rounded p-3">
                    <div className="text-[var(--color-text-muted)] text-sm capitalize">Institution</div>
                    <div className="text-[var(--color-text)] font-medium">{contextField(revampData.context_json, 'institution', 'university')}</div>
                  </div>
                  <div className="bg-[var(--color-surface-muted)] rounded p-3">
                    <div className="text-[var(--color-text-muted)] text-sm capitalize">Course Of Study</div>
                    <div className="text-[var(--color-text)] font-medium">{contextField(revampData.context_json, 'course_of_study', 'course_title')}</div>
                  </div>
                </div>
                {/* Third row: Course Description (full width) */}
                <div className="bg-[var(--color-surface-muted)] rounded p-3">
                  <div className="text-[var(--color-text-muted)] text-sm capitalize">Course Description</div>
                  <div className="text-[var(--color-text)] font-medium whitespace-pre-wrap">{contextField(revampData.context_json, 'course_description')}</div>
                </div>
              </div>
            ) : (
              <div className={`grid ${window.innerWidth < 768 ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}`}>
                {Object.entries(revampData.context_json)
                  .filter(([key]) => !['guide_statement', 'purpose', 'course_title', 'university'].includes(key))
                  .map(([key, value]) => (
                    <div key={key} className="bg-[var(--color-surface-muted)] rounded p-3">
                      <div className="text-[var(--color-text-muted)] text-sm capitalize">{key.replace(/_/g, ' ')}</div>
                      <div className="text-[var(--color-text)] font-medium">{value || 'Not specified'}</div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Content Comparison */}
        {enhancedPdfUrl && enhancedDocxUrl ? (
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setEnhancedFormat('pdf')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${enhancedFormat === 'pdf' ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] border border-[var(--color-border)]'}`}
            >
              PDF Preview
            </button>
            <button
              type="button"
              onClick={() => setEnhancedFormat('docx')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${enhancedFormat === 'docx' ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] border border-[var(--color-border)]'}`}
            >
              DOCX Preview
            </button>
          </div>
        ) : null}
        <div className={`grid ${window.innerWidth < 768 ? 'grid-cols-1 gap-4' : 'grid-cols-1 lg:grid-cols-2 gap-6'}`}>
          {/* Original */}
          <div className={`elevated-card rounded-lg ${window.innerWidth < 768 ? 'p-3' : 'p-6'}`}>
            <h3 className={`text-xl font-semibold text-[var(--color-text)] mb-4 flex items-center ${window.innerWidth < 768 ? 'text-lg' : ''}`}>
              <span className="w-3 h-3 bg-red-500 rounded-full mr-3"></span>
              Original {getDocTypeTitle(docType)}
            </h3>
            {originalFileUrl ? (
              <DocumentFileViewer
                url={originalFileUrl}
                fallbackText={original || 'Original text not available'}
                title={`Original ${getDocTypeTitle(docType)}`}
              />
            ) : (
              <div className="bg-[var(--color-surface-muted)] rounded-lg p-4 h-96 overflow-y-auto border border-[var(--color-border)]">
                <pre className="text-[var(--color-text-secondary)] whitespace-pre-wrap text-sm leading-relaxed">
                  {original || 'Original text not available'}
                </pre>
              </div>
            )}
          </div>

          {/* Revamped */}
          <div className={`elevated-card rounded-lg ${window.innerWidth < 768 ? 'p-3' : 'p-6'}`}>
            <h3 className={`text-xl font-semibold text-[var(--color-text)] mb-4 flex items-center ${window.innerWidth < 768 ? 'text-lg' : ''}`}>
              <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
              Enhanced {getDocTypeTitle(docType)}
            </h3>
            {enhancedFileUrl ? (
              <DocumentFileViewer
                key={enhancedFileUrl}
                url={enhancedFileUrl}
                fallbackText={renderEnhancedText()}
                title={`Enhanced ${getDocTypeTitle(docType)}`}
              />
            ) : (
              <div className="bg-[var(--color-surface-muted)] rounded-lg p-4 h-96 overflow-y-auto border border-[var(--color-border)]">
                <pre className="text-[var(--color-text-secondary)] whitespace-pre-wrap text-sm leading-relaxed">
                  {renderEnhancedText()}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className={`${window.innerWidth < 768 ? 'mt-8 flex flex-row gap-2' : 'mt-8 flex justify-center space-x-4'}`}>
          <button
            onClick={handleDownloadDocx}
            className={`brand-gradient-btn ${window.innerWidth < 768 ? 'px-3 py-2 rounded-lg font-medium' : 'px-3 py-2 rounded-lg font-medium'}`}
          >
            Download Doc
          </button>
          <button
            onClick={handleDownloadPDF}
            className={`brand-gradient-btn ${window.innerWidth < 768 ? 'px-3 py-2 rounded-lg font-medium' : 'px-3 py-2 rounded-lg font-medium'}`}
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevampResultPage;